import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  useMarketplaceDefinition,
  useCreateMarketplaceDefinition,
  useUpdateMarketplaceDefinition,
  useSetMarketplaceDefinitionWorkStatus,
  useTemplateDefinitions,
} from '../hooks/useMarketplaceDefinitions';
import { useUserNames } from '../hooks/useUserNames';
import { UserProviderChip } from '../components/UserProviderChip';
import { apiClient, storeClient, endpoints } from '../api/client';
import { useCurrencies, CurrencyOption } from '../hooks/useCurrencies';
import { useCountries } from '../hooks/useCountries';
import { aiValidationService, ValidationSuggestion } from '../services/aiValidationService';
import { FIELD_REGISTRY, getRegistryFor, PageKind } from '../types/fieldRegistry';
import {
  MarketplaceDefinition,
  MarketplaceDefinitionConfig,
  FieldDefinition,
  PostProcessingConfig,
} from '../types/marketplaceDefinition';
import { getCurrentProviderId } from '../utils/currentUser';
import { fromTemplateDomain, toTemplateDomain } from '../utils/templateDomain';
import { applyFieldUpdates } from '../utils/definitionFieldUpdates';

/** Canonical seller page key is seller_page; fold legacy seller_profile into it. */
function normalizePagesForEditor(pages: Record<string, any> | undefined): Record<string, any> {
  if (!pages || typeof pages !== 'object') {
    return {};
  }
  const next = { ...pages };
  const legacyProfile = next.seller_profile;
  const sellerPage = next.seller_page;
  if (legacyProfile && typeof legacyProfile === 'object') {
    const basePage = sellerPage && typeof sellerPage === 'object' ? sellerPage : {};
    const profileUrl =
      typeof legacyProfile.url_pattern === 'string' ? legacyProfile.url_pattern.trim() : '';
    next.seller_page = {
      ...basePage,
      ...legacyProfile,
      page_type: 'seller_page',
      url_pattern: profileUrl || basePage.url_pattern,
      fields: {
        ...(basePage.fields && typeof basePage.fields === 'object' ? basePage.fields : {}),
        ...(legacyProfile.fields && typeof legacyProfile.fields === 'object'
          ? legacyProfile.fields
          : {}),
      },
    };
    delete next.seller_profile;
  } else if (sellerPage && typeof sellerPage === 'object') {
    const pt = sellerPage.page_type;
    next.seller_page = {
      ...sellerPage,
      page_type: pt === 'seller' || !pt ? 'seller_page' : pt,
    };
  }
  return next;
}

// Seller navigation step type descriptions
const STEP_TYPE_DESCRIPTIONS: Record<string, string> = {
  extract:
    'Extract one seller URL. CSS reads an element attribute (for example href). JSONPath reads a field from a JSON script on the product page.',
  iterate:
    'Extract ALL matching elements and get their attribute values. Use when multiple items exist (e.g., multiple seller links).',
  follow:
    'Click a link to navigate to a new page, then extract from that page. Use "Capture URL" to get the final URL after JavaScript navigation (e.g., for Temu seller links).',
  click:
    'Click an element on the current page to reveal hidden content (e.g., show offers panel). Stays on same page.',
  field:
    'Reference a field value that was already extracted from the product page (e.g., use seller_store_url field).',
};

function isJsonPathField(field: { selector_type?: string } | null | undefined): boolean {
  return String(field?.selector_type || '').toLowerCase() === 'json_path';
}

function isJsonPathNavStep(
  step: { type?: string; selector_type?: string } | null | undefined
): boolean {
  return (step?.type || 'extract') === 'extract' && isJsonPathField(step);
}

// Locale format validation: lowercase language (2-3 chars), hyphen, uppercase country (2 chars)
// Examples: en-US, de-DE, fr-FR, pt-BR, zh-CN
const LOCALE_REGEX = /^[a-z]{2,3}-[A-Z]{2}$/;

const getLocaleError = (locale: string | undefined | null): string | null => {
  if (!locale || locale.trim() === '') return null;
  if (!LOCALE_REGEX.test(locale)) {
    return 'Format: lowercase language, hyphen, uppercase country (e.g., en-US, de-DE)';
  }
  return null;
};

const FALLBACK_CURRENCY_CODES: CurrencyOption[] = [
  { code: 'USD', name: 'US Dollar' },
  { code: 'EUR', name: 'Euro' },
  { code: 'GBP', name: 'British Pound Sterling' },
  { code: 'JPY', name: 'Japanese Yen' },
  { code: 'AUD', name: 'Australian Dollar' },
  { code: 'CAD', name: 'Canadian Dollar' },
];
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  LinearProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ButtonGroup,
  Popper,
  Grow,
  Paper,
  ClickAwayListener,
  MenuList,
  InputAdornment,
  Popover,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  ArrowDropDown as ArrowDropDownIcon,
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  AutoFixHigh as AutoFixHighIcon,
  ContentPaste as PasteIcon,
  History as HistoryIcon,
  Restore as RestoreIcon,
  OpenInNew as OpenInNewIcon,
  ContentCopy as ContentCopyIcon,
  Info as InfoIcon,
  HelpOutline as HelpIcon,
  Science as TestIcon,
} from '@mui/icons-material';
import RegexTesterDialog from '../components/RegexTesterDialog';
import PageActionsEditor from '../components/PageActionsEditor';
import JsonIslandsEditor from '../components/JsonIslandsEditor';

const DefinitionEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [isNew, setIsNew] = useState(false);
  const [hasEmptyOriginalConfig, setHasEmptyOriginalConfig] = useState(false); // Track if loaded config was empty (fully inherited)
  const [configError, setConfigError] = useState<string>('');
  const [saveFieldError, setSaveFieldError] = useState<string>('');
  const [isAiValidating, setIsAiValidating] = useState(false);
  const [validationProgress, setValidationProgress] = useState<number>(0);
  const [validationStep, setValidationStep] = useState<string>('');
  const [validationSummary, setValidationSummary] = useState<any | null>(null);
  const [validationSuggestions, setValidationSuggestions] = useState<ValidationSuggestion[] | null>(
    null
  );
  const [validationError, setValidationError] = useState<string | null>(null);
  const [aiAccordionExpanded, setAiAccordionExpanded] = useState(false);
  const [validationAbortController, setValidationAbortController] =
    useState<AbortController | null>(null);
  const [currentValidationId, setCurrentValidationId] = useState<string | null>(null);
  const [enhancedFieldSet, setEnhancedFieldSet] = useState<Set<string>>(new Set());
  // MR 3: per-suggestion approve/reject state. Indexed by the suggestion's
  // position in `validationSuggestions`. Approved suggestions have already
  // mutated `config` locally — the user still has to click Save to persist.
  // Dismissed suggestions just disappear from the panel.
  const [appliedSuggestions, setAppliedSuggestions] = useState<Set<number>>(new Set());
  const [dismissedSuggestions, setDismissedSuggestions] = useState<Set<number>>(new Set());
  const [showPasteDialog, setShowPasteDialog] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [expandedSellerFields, setExpandedSellerFields] = useState<Set<string>>(new Set());

  // Version history state
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [versions, setVersions] = useState<any[]>([]);
  const [isLoadingVersions, setIsLoadingVersions] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<any | null>(null);

  // Save button dropdown state
  const [saveMenuOpen, setSaveMenuOpen] = useState(false);
  const saveMenuAnchorRef = React.useRef<HTMLDivElement>(null);
  const [saveAndContinue, setSaveAndContinue] = useState<boolean>(() => {
    // Load preference from localStorage
    const saved = localStorage.getItem('definitionEditor.saveAndContinue');
    return saved === 'true';
  });

  // Stats popover state
  const [statsAnchorEl, setStatsAnchorEl] = useState<HTMLButtonElement | null>(null);
  const statsPopoverOpen = Boolean(statsAnchorEl);

  // Helper to format dates using locale
  const formatDate = (dateString: string | undefined | null): string => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      return date.toLocaleString();
    } catch {
      return dateString;
    }
  };

  // Use real API hooks
  const {
    data: existingDefinition,
    isLoading: isLoadingDefinition,
    refetch: refetchDefinition,
  } = useMarketplaceDefinition(id || '');
  const createDefinition = useCreateMarketplaceDefinition();
  const updateDefinition = useUpdateMarketplaceDefinition();
  const clearWorkStatus = useSetMarketplaceDefinitionWorkStatus();
  const { data: userNames = {}, isLoading: userNamesLoading } = useUserNames();
  const [currentProviderId, setCurrentProviderId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getCurrentProviderId().then(providerId => {
      if (!cancelled) setCurrentProviderId(providerId);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch available templates for parent selection
  const { data: templates = [] } = useTemplateDefinitions();

  // Fetch currencies from store service (falls back to a small hardcoded list)
  const { data: fetchedCurrencies } = useCurrencies();
  const currencyCodes: CurrencyOption[] =
    fetchedCurrencies && fetchedCurrencies.length > 0 ? fetchedCurrencies : FALLBACK_CURRENCY_CODES;

  // Fetch the full country list from the store service
  const { data: fetchedCountries } = useCountries();
  const countryCodes = fetchedCountries ?? [];

  // Parent definition config for inheritance comparison
  const [parentConfig, setParentConfig] = useState<MarketplaceDefinitionConfig | null>(null);
  const [parentDefinitionName, setParentDefinitionName] = useState<string>('');
  const [isLoadingParent, setIsLoadingParent] = useState(false);
  // Raw config stored in child (from API) - used to detect explicit overrides. Only keys the user
  // explicitly set are stored; template changes propagate for keys not in this object.
  const [childStoredConfig, setChildStoredConfig] = useState<Record<string, any> | null>(null);

  // Regex tester dialog state
  const [regexTesterOpen, setRegexTesterOpen] = useState(false);
  const [regexTesterPattern, setRegexTesterPattern] = useState('');
  const [regexTesterTitle, setRegexTesterTitle] = useState('Test Regex Pattern');
  const [regexTesterMode, setRegexTesterMode] = useState<'match' | 'replace'>('match');
  const [regexTesterReplacement, setRegexTesterReplacement] = useState('');
  const [regexTesterMatchOnlyAtStart, setRegexTesterMatchOnlyAtStart] = useState(false);

  // Initialize with empty definition or existing data
  const [definition, setDefinition] = useState<MarketplaceDefinition>({
    id: '',
    marketplace: '',
    name: '',
    version: '1.0.0',
    domain: '',
    is_active: true,
    brand_id: null,
    product_id: null,
    overall_success_rate: 0.0,
    ai_maintenance_level: 'low',
    created_at: '',
    updated_at: '',
    login_username: undefined,
    login_password: undefined,
    login_username_selector: undefined,
    login_password_selector: undefined,
    login_submit_selector: undefined,
  });

  // Configuration state
  const [config, setConfig] = useState<MarketplaceDefinitionConfig>({
    pages: {},
    use_common_headers: true,
    use_common_user_agents: true,
    additional_headers: {},
    additional_user_agents: [],
    use_playwright: false,
    use_proxy: undefined, // undefined means use global proxy setting
    search_url_template: '',
    two_stage_scraping: false,
    rate_limit: 5,
    delay_between_requests: 1.0,
    allowed_image_hosts: [],
  });

  useEffect(() => {
    if (id && id !== 'new') {
      setIsNew(false);
    } else {
      setIsNew(true);
    }
  }, [id]);

  // Load existing definition data when available
  useEffect(() => {
    if (existingDefinition && !isNew) {
      setDefinition(existingDefinition);
      if (existingDefinition.config) {
        // Ensure the config has the required structure
        const configData = existingDefinition.config as any;

        // Check if the original config is essentially empty (fully inherited from parent)
        // An empty config means the child should inherit all config from parent
        const scrapingConfigKeys = [
          'use_playwright',
          'two_stage_scraping',
          'use_proxy',
          'search_url_template',
          'rate_limit',
          'delay_between_requests',
          'use_common_headers',
          'use_common_user_agents',
          'additional_headers',
          'additional_user_agents',
          'allowed_image_hosts',
          'locale',
          'capture_images',
          'max_stage2_products',
          'channel',
          'pages',
        ];
        const isConfigEmpty = !scrapingConfigKeys.some(key => configData[key] !== undefined);
        setHasEmptyOriginalConfig(isConfigEmpty);

        // Store raw child config for override detection (inheritance)
        setChildStoredConfig(existingDefinition.parent_definition_id ? { ...configData } : null);

        if (configData.pages) {
          setConfig({
            ...configData,
            pages: normalizePagesForEditor(configData.pages),
          } as MarketplaceDefinitionConfig);
        } else {
          // If config doesn't have pages structure, initialize with empty pages
          // Migrate old headers/user_agents to new structure if present
          const useCommonHeaders =
            configData.use_common_headers !== undefined ? configData.use_common_headers : true;
          const useCommonUserAgents =
            configData.use_common_user_agents !== undefined
              ? configData.use_common_user_agents
              : true;
          const additionalHeaders =
            configData.additional_headers ||
            (configData.headers && !useCommonHeaders ? configData.headers : {});
          const additionalUserAgents =
            configData.additional_user_agents ||
            (configData.user_agents && !useCommonUserAgents ? configData.user_agents : []);

          setConfig({
            pages: {},
            use_common_headers: useCommonHeaders,
            use_common_user_agents: useCommonUserAgents,
            additional_headers: additionalHeaders,
            additional_user_agents: additionalUserAgents,
            use_playwright: configData.use_playwright || false,
            use_proxy: configData.use_proxy, // undefined means use global proxy setting
            search_url_template: configData.search_url_template || '',
            two_stage_scraping: configData.two_stage_scraping || false,
            rate_limit: configData.rate_limit || 5,
            delay_between_requests: configData.delay_between_requests || 1.0,
            allowed_image_hosts: configData.allowed_image_hosts || [],
          });
        }
        // Merge login selectors from config.login into definition state for the form
        if (configData.login) {
          setDefinition(prev => ({
            ...prev,
            login_username_selector: configData.login.username_selector ?? undefined,
            login_password_selector: configData.login.password_selector ?? undefined,
            login_submit_selector: configData.login.submit_selector ?? undefined,
          }));
        }
      } else {
        // No config at all - this is fully inherited
        setHasEmptyOriginalConfig(true);
        setChildStoredConfig(existingDefinition.parent_definition_id ? {} : null);
      }

      // Fetch live success rate and last_tested
      if (existingDefinition.id) {
        fetch(`/api/v1/scraper/statistics/definitions/${existingDefinition.id}/success-rate`)
          .then(r => r.json())
          .then(data => {
            setDefinition(prev => ({
              ...prev,
              overall_success_rate:
                typeof data.success_rate === 'number'
                  ? data.success_rate / 100
                  : prev.overall_success_rate,
              last_tested: data.last_tested || prev.last_tested,
            }));
          })
          .catch(() => {
            // ignore errors
          });
      }
    }
  }, [existingDefinition, isNew]);

  // Store service state
  // Removed brand/product selects: these belong to the Scraping Task form, not the marketplace definition editor
  // Note: brands/products state retained for possible future UI, but avoid unused var errors
  const [, setBrands] = useState<Array<{ id: string; name: string }>>([]);
  const [, setProducts] = useState<Array<{ id: string; name: string; keywords?: string[] }>>([]);
  const [, setLoadingBrands] = useState(false);
  const [, setLoadingProducts] = useState(false);

  const sellerNavigationMode = (config as any)?.seller_navigation?.mode || 'multiple';

  useEffect(() => {
    // Load first page of brands on mount
    const loadBrands = async () => {
      setLoadingBrands(true);
      try {
        const res = await storeClient.get(endpoints.storeBrands());
        const items = res?.data?.items || res?.data?.content || res?.data || [];
        setBrands(items.map((b: any) => ({ id: b.id || b._id || String(b.id), name: b.name })));
      } catch (e) {
        // non-fatal
      } finally {
        setLoadingBrands(false);
      }
    };
    loadBrands();
  }, []);

  useEffect(() => {
    // Load products when brand changes
    const loadProducts = async (brandName: string) => {
      setLoadingProducts(true);
      try {
        const res = await storeClient.get(endpoints.storeProductsByBrandName(brandName, 0, 500));
        const items = res?.data?.items || res?.data?.content || res?.data || [];
        setProducts(
          items.map((p: any) => ({
            id: p.id || p._id || String(p.id),
            name: p.name,
            keywords: p.keywords,
          }))
        );
      } catch (e) {
        setProducts([]);
      } finally {
        setLoadingProducts(false);
      }
    };
    if ((definition as any).brand_name || (definition as any).brand) {
      loadProducts(((definition as any).brand_name || (definition as any).brand) as string);
    } else {
      setProducts([]);
    }
  }, [definition.brand_id, definition]);

  // Fetch parent definition config when parent_definition_id changes
  useEffect(() => {
    const fetchParentConfig = async () => {
      if (!definition.parent_definition_id) {
        setParentConfig(null);
        setParentDefinitionName('');
        return;
      }

      setIsLoadingParent(true);
      try {
        const response = await apiClient.get(endpoints.definition(definition.parent_definition_id));
        const parentDef = response.data;
        if (parentDef?.config) {
          const fetchedParentConfig = parentDef.config as MarketplaceDefinitionConfig;
          setParentConfig(fetchedParentConfig);
          setParentDefinitionName(parentDef.name || 'Parent Template');

          // For child definitions: merge parent + childStoredConfig for display.
          // Template changes propagate: we only store overrides in the child, so keys not in
          // childStoredConfig come from the parent (and update when template changes).
          if (definition.parent_definition_id) {
            const childOverrides = childStoredConfig ?? {};
            const parentCopy = JSON.parse(JSON.stringify(fetchedParentConfig));
            const merged = JSON.parse(JSON.stringify(parentCopy));
            // Apply child overrides (child wins, same as backend deep_merge)
            const deepMergeChild = (target: any, source: any) => {
              if (source == null) return;
              for (const [k, v] of Object.entries(source)) {
                if (
                  typeof v === 'object' &&
                  v !== null &&
                  !Array.isArray(v) &&
                  typeof target[k] === 'object' &&
                  target[k] !== null &&
                  !Array.isArray(target[k])
                ) {
                  deepMergeChild(target[k], v);
                } else {
                  target[k] = JSON.parse(JSON.stringify(v));
                }
              }
            };
            deepMergeChild(merged, childOverrides);
            setConfig(merged);
          } else if (isNew || hasEmptyOriginalConfig) {
            // New definition or fully inherited: initialize from parent
            setConfig(prev => {
              const parentConfigCopy = JSON.parse(JSON.stringify(fetchedParentConfig));
              return {
                ...prev,
                use_playwright: parentConfigCopy.use_playwright,
                two_stage_scraping: parentConfigCopy.two_stage_scraping,
                use_proxy: parentConfigCopy.use_proxy,
                search_url_template: parentConfigCopy.search_url_template,
                rate_limit: parentConfigCopy.rate_limit,
                delay_between_requests: parentConfigCopy.delay_between_requests,
                use_common_headers: parentConfigCopy.use_common_headers,
                use_common_user_agents: parentConfigCopy.use_common_user_agents,
                additional_headers: parentConfigCopy.additional_headers,
                additional_user_agents: parentConfigCopy.additional_user_agents,
                allowed_image_hosts: parentConfigCopy.allowed_image_hosts,
                locale: parentConfigCopy.locale,
                capture_images: parentConfigCopy.capture_images,
                max_stage2_products: parentConfigCopy.max_stage2_products,
                channel: parentConfigCopy.channel,
                seller_navigation: parentConfigCopy.seller_navigation,
                pages: prev.pages,
              } as any;
            });
          }
        }
      } catch (e) {
        console.error('Failed to fetch parent definition:', e);
        setParentConfig(null);
        setParentDefinitionName('');
      } finally {
        setIsLoadingParent(false);
      }
    };

    fetchParentConfig();
  }, [definition.parent_definition_id, isNew, hasEmptyOriginalConfig, childStoredConfig]);

  const getPageConfigKey = (
    pageType: 'search_results' | 'product_listing' | 'seller_page'
  ): 'search_results' | 'product_listing' | 'seller_page' => pageType;

  // Helper to get parent page config
  const getParentPageConfig = (
    pageType: 'search_results' | 'product_listing' | 'seller_page'
  ): any => {
    if (!parentConfig?.pages) return undefined;
    const key = getPageConfigKey(pageType);
    return (
      parentConfig.pages[key] ??
      (pageType === 'seller_page' ? parentConfig.pages?.seller_page : undefined)
    );
  };

  // Helper to check if a page config section is inherited (matches parent exactly)
  const isPageConfigInherited = (
    pageType: 'search_results' | 'product_listing' | 'seller_page'
  ): boolean => {
    const parentPage = getParentPageConfig(pageType);
    if (!parentConfig || !parentPage) return false;
    const key = getPageConfigKey(pageType);
    const currentPage =
      config.pages?.[key] ?? (pageType === 'seller_page' ? config.pages?.seller_page : undefined);

    // If no current page config, it's inherited from parent
    if (!currentPage) return true;

    // Compare JSON representations (excluding fields that might be dynamically added)
    return JSON.stringify(currentPage) === JSON.stringify(parentPage);
  };

  // Helper to check if a specific field is inherited (for future field-level UI)
  const _isFieldInherited = (
    pageType: 'search_results' | 'product_listing' | 'seller_page',
    fieldKey: string
  ): boolean => {
    const key = getPageConfigKey(pageType);
    const parentPage = getParentPageConfig(pageType);
    if (!parentConfig || !parentPage?.fields?.[fieldKey]) return false;
    const currentPage =
      config.pages?.[key] ?? (pageType === 'seller_page' ? config.pages?.seller_page : undefined);
    const currentField = currentPage?.fields?.[fieldKey];
    const parentField = parentPage.fields[fieldKey];

    // If no current field config, it's inherited
    if (!currentField) return true;

    return JSON.stringify(currentField) === JSON.stringify(parentField);
  };
  // Suppress unused warning - available for future field-level inheritance UI
  void _isFieldInherited;

  // Helper to reset a page config section to inherited value
  const resetPageToInherited = (pageType: 'search_results' | 'product_listing' | 'seller_page') => {
    const parentPage = getParentPageConfig(pageType);
    if (!parentPage) return;

    const key = getPageConfigKey(pageType);
    setConfig(prev => ({
      ...prev,
      pages: {
        ...prev.pages,
        [key]: JSON.parse(JSON.stringify(parentPage)),
      },
    }));
  };

  // Helper to reset a specific field to inherited value (for future field-level UI)
  const _resetFieldToInherited = (
    pageType: 'search_results' | 'product_listing' | 'seller_page',
    fieldKey: string
  ) => {
    const key = getPageConfigKey(pageType);
    const parentPage = getParentPageConfig(pageType);
    if (!parentPage?.fields?.[fieldKey]) return;

    setConfig(prev => ({
      ...prev,
      pages: {
        ...prev.pages,
        [key]: {
          ...prev.pages?.[key],
          fields: {
            ...prev.pages?.[key]?.fields,
            [fieldKey]: JSON.parse(JSON.stringify(parentPage.fields[fieldKey])),
          },
        },
      },
    }));
  };
  // Suppress unused warning - available for future field-level inheritance UI
  void _resetFieldToInherited;

  // Helper to check if seller_navigation config is inherited
  const isSellerNavigationInherited = (): boolean => {
    if (!parentConfig) return false;

    const currentNav = (config as any).seller_navigation;
    const parentNav = (parentConfig as any).seller_navigation;

    // Both undefined/null = inherited (both empty)
    if (!currentNav && !parentNav) return true;

    // Child has nav but parent doesn't = overridden
    if (currentNav && !parentNav) return false;

    // Parent has nav but child doesn't = inherited (child uses parent's)
    if (!currentNav && parentNav) return true;

    // Both have nav - compare them
    return JSON.stringify(currentNav) === JSON.stringify(parentNav);
  };

  // Helper to reset seller_navigation to inherited value
  const resetSellerNavigationToInherited = () => {
    if (!parentConfig) return;

    const parentNav = (parentConfig as any).seller_navigation;

    setConfig(prev => {
      if (parentNav) {
        // Parent has seller_navigation - copy it
        return {
          ...prev,
          seller_navigation: JSON.parse(JSON.stringify(parentNav)),
        } as any;
      } else {
        // Parent has no seller_navigation - delete child's to inherit "none"
        const updated = { ...prev };
        delete (updated as any).seller_navigation;
        return updated;
      }
    });
  };

  // Helper to check if scraping configuration is inherited.
  // Inherited = no overrides stored (childStoredConfig has no scraping keys) AND current config
  // matches parent. If user has edited in this session, config will differ and we show Overridden.
  // search_url_template is tracked separately so locale children can override it without
  // forking playwright/proxy/rate-limit settings.
  const isScrapingConfigInherited = (): boolean => {
    if (!parentConfig || !definition.parent_definition_id) return false;

    const scrapingConfigKeys = [
      'use_playwright',
      'two_stage_scraping',
      'use_proxy',
      'rate_limit',
      'delay_between_requests',
      'use_common_headers',
      'use_common_user_agents',
      'additional_headers',
      'additional_user_agents',
      'allowed_image_hosts',
      'capture_images',
      'max_stage2_products',
      'channel',
    ];

    for (const key of scrapingConfigKeys) {
      // Stored override
      if (childStoredConfig && key in childStoredConfig) return false;
      // Current value differs from parent (user edited in this session)
      const cur = (config as any)[key];
      const par = (parentConfig as any)[key];
      if (JSON.stringify(cur) !== JSON.stringify(par)) return false;
    }
    return true;
  };

  // Helper to reset scraping configuration to inherited values
  const resetScrapingConfigToInherited = () => {
    if (!parentConfig) return;

    const scrapingConfigKeys = [
      'use_playwright',
      'two_stage_scraping',
      'use_proxy',
      'rate_limit',
      'delay_between_requests',
      'use_common_headers',
      'use_common_user_agents',
      'additional_headers',
      'additional_user_agents',
      'allowed_image_hosts',
      'locale',
      'capture_images',
      'max_stage2_products',
      'channel',
    ];

    setConfig(prev => {
      const updated = { ...prev };
      for (const key of scrapingConfigKeys) {
        const parentVal = (parentConfig as any)[key];
        if (parentVal !== undefined) {
          (updated as any)[key] = JSON.parse(JSON.stringify(parentVal));
        } else {
          delete (updated as any)[key];
        }
      }
      return updated;
    });
    // Clear these keys from childStoredConfig so "Inherited" shows immediately
    setChildStoredConfig(prev => {
      if (!prev) return prev;
      const next = { ...prev };
      for (const key of scrapingConfigKeys) delete next[key];
      return Object.keys(next).length > 0 ? next : null;
    });
  };

  const isSearchUrlTemplateInherited = (): boolean => {
    if (!parentConfig || !definition.parent_definition_id) return false;
    const parentVal = String((parentConfig as any).search_url_template || '').trim();
    if (
      childStoredConfig &&
      Object.prototype.hasOwnProperty.call(childStoredConfig, 'search_url_template')
    ) {
      const stored = String((childStoredConfig as any).search_url_template || '').trim();
      if (stored !== parentVal) return false;
    }
    return String(config.search_url_template || '').trim() === parentVal;
  };

  const resetSearchUrlTemplateToInherited = () => {
    if (!parentConfig) return;
    const parentVal = (parentConfig as any).search_url_template;
    setConfig(prev => ({
      ...prev,
      search_url_template: parentVal || '',
    }));
    setChildStoredConfig(prev => {
      if (!prev) return prev;
      const next = { ...prev };
      delete next.search_url_template;
      return Object.keys(next).length > 0 ? next : null;
    });
  };

  // Helper to generate InputProps for selector fields with copy-to-clipboard functionality
  const getSelectorInputProps = (selectorValue: string | undefined) => ({
    endAdornment: selectorValue ? (
      <InputAdornment position="end">
        <Tooltip title="Copy as document.querySelectorAll()">
          <IconButton
            size="small"
            onClick={() => {
              const selector = selectorValue.trim();
              if (selector) {
                const escaped = selector.replace(/'/g, "\\'");
                navigator.clipboard.writeText(`document.querySelectorAll('${escaped}')`);
              }
            }}
            edge="end"
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </InputAdornment>
    ) : null,
  });

  // Helper to open the regex tester dialog
  const openRegexTester = (
    pattern: string,
    title: string,
    mode: 'match' | 'replace' = 'match',
    replacement: string = '',
    matchOnlyAtStart = false
  ) => {
    setRegexTesterPattern(pattern);
    setRegexTesterTitle(title);
    setRegexTesterMode(mode);
    setRegexTesterReplacement(replacement);
    setRegexTesterMatchOnlyAtStart(matchOnlyAtStart);
    setRegexTesterOpen(true);
  };

  // Helper to generate InputProps for regex fields with test button
  const getRegexInputProps = (
    regexValue: string | undefined,
    title: string = 'Test Regex Pattern',
    mode: 'match' | 'replace' = 'match',
    replacement: string = '',
    matchOnlyAtStart = false
  ) => ({
    endAdornment: (
      <InputAdornment position="end">
        <Tooltip title="Test this regex pattern">
          <IconButton
            size="small"
            onClick={() =>
              openRegexTester(regexValue || '', title, mode, replacement, matchOnlyAtStart)
            }
            edge="end"
          >
            <TestIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </InputAdornment>
    ),
  });

  // Check if definition has any local overrides
  const hasLocalOverrides = (): boolean => {
    if (!parentConfig) return false;

    const pageTypes: Array<'search_results' | 'product_listing' | 'seller_page'> = [
      'search_results',
      'product_listing',
      'seller_page',
    ];

    const pageOverrides = pageTypes.some(pt => !isPageConfigInherited(pt));
    const navOverrides = !isSellerNavigationInherited();
    const scrapingOverrides = !isScrapingConfigInherited();
    const searchUrlOverrides = !isSearchUrlTemplateInherited();

    return pageOverrides || navOverrides || scrapingOverrides || searchUrlOverrides;
  };

  const isFieldValidForPage = (fieldKey: string, pageType: PageKind): boolean => {
    const validFields = getRegistryFor(pageType);
    return validFields.some(f => f.key === fieldKey);
  };

  const validateFieldsAgainstRegistry = (): { valid: boolean; errors: string[] } => {
    const errors: string[] = [];

    // Validate each page type
    const pageTypes: Array<'search_results' | 'product_listing' | 'seller_page'> = [
      'search_results',
      'product_listing',
      'seller_page',
    ];

    pageTypes.forEach(pageType => {
      const key = getPageConfigKey(pageType);
      const page =
        config.pages?.[key] ?? (pageType === 'seller_page' ? config.pages?.seller_page : undefined);
      if (!page || !page.fields) return;

      // Get valid fields for this page type from registry
      const validFields = getRegistryFor(pageType as PageKind);
      const validFieldKeys = new Set(validFields.map(f => f.key));

      // Check each field in the definition
      Object.keys(page.fields).forEach(fieldKey => {
        if (!validFieldKeys.has(fieldKey)) {
          errors.push(
            `${pageType}: field '${fieldKey}' is not allowed by field registry. ` +
              `Valid fields: ${Array.from(validFieldKeys).join(', ')}`
          );
        }
      });
    });

    return {
      valid: errors.length === 0,
      errors,
    };
  };

  const handleRemoveInvalidFields = () => {
    let removedAny = false;
    setConfig(prevConfig => {
      if (!prevConfig) {
        return prevConfig;
      }

      const nextPages: any = { ...(prevConfig.pages || {}) };
      const pageKinds: PageKind[] = ['search_results', 'product_listing', 'seller_page'];

      pageKinds.forEach(pageType => {
        const page = nextPages?.[pageType];
        if (!page || typeof page !== 'object') {
          return;
        }

        const fields = (page as any).fields;
        if (!fields || typeof fields !== 'object' || Array.isArray(fields)) {
          return;
        }

        const validFieldKeys = new Set(getRegistryFor(pageType).map(field => field.key));
        const entries = Object.entries(fields as Record<string, any>);
        const filteredEntries = entries.filter(([key]) => validFieldKeys.has(key));

        if (filteredEntries.length !== entries.length) {
          removedAny = true;
          nextPages[pageType] = {
            ...page,
            fields: Object.fromEntries(filteredEntries),
          };
        }
      });

      if (!removedAny) {
        return prevConfig;
      }

      return {
        ...prevConfig,
        pages: nextPages,
      };
    });

    setConfigError(
      removedAny ? '' : 'No invalid fields were found to remove. Please refresh or check the form.'
    );
  };

  const handleSave = async (shouldNavigate: boolean = true) => {
    const name = (definition.name || '').trim();
    let domain = (definition.domain || '').trim();
    if (!name) {
      setSaveFieldError('Name is required.');
      return;
    }
    if (!domain) {
      setSaveFieldError('Domain is required.');
      return;
    }
    if (definition.is_template) {
      domain = toTemplateDomain(domain);
    }
    if (name !== definition.name || domain !== definition.domain) {
      setDefinition(prev => ({ ...prev, name, domain }));
    }
    setSaveFieldError('');
    setIsLoading(true);
    try {
      setConfigError('');

      // Validate fields against registry first
      const validation = validateFieldsAgainstRegistry();
      if (!validation.valid) {
        setConfigError(
          'Invalid field configuration:\n' + validation.errors.map(e => `• ${e}`).join('\n')
        );
        setIsLoading(false);
        return;
      }

      // Ensure every field object has a valid 'name' matching its key before saving
      const normalizeForSave = (cfg: MarketplaceDefinitionConfig): MarketplaceDefinitionConfig => {
        const legacyToCanonical: Record<string, string> = {
          sellerStoreUrl: 'seller_store_url',
        };
        const pageKeys: Array<'search_results' | 'product_listing' | 'seller_page'> = [
          'search_results',
          'product_listing',
          'seller_page',
        ];
        const next: any = { ...cfg, pages: normalizePagesForEditor(cfg.pages || {}) };
        if (next.seller_navigation) {
          const sellerNav = { ...next.seller_navigation };
          if ('dedupe_by' in sellerNav) {
            delete sellerNav.dedupe_by;
          }
          next.seller_navigation = sellerNav;
        }

        pageKeys.forEach(pk => {
          const page: any = next.pages[pk];
          if (!page || !page.fields) return;
          const newFields: Record<string, any> = {};
          Object.entries(page.fields as Record<string, any>).forEach(([k, v]) => {
            const canonicalKey = legacyToCanonical[k] || k;
            const fieldObj: any = { ...(v || {}) };
            fieldObj.name = canonicalKey; // validator expects 'name'
            newFields[canonicalKey] = fieldObj;
          });
          next.pages[pk] = { ...page, fields: newFields, page_type: pk };
        });
        return next;
      };

      // Include locale/country/currency in config so they are stored in the definition JSON
      // and appear in copied config. Task execution reads definition column and uses definition.get('locale').
      const loginPayload: Record<string, string> = {};
      if (definition.login_username != null && definition.login_username !== '')
        loginPayload.username = definition.login_username;
      if (definition.login_password != null && definition.login_password !== '')
        loginPayload.password = definition.login_password;
      if (definition.login_username_selector)
        loginPayload.username_selector = definition.login_username_selector;
      if (definition.login_password_selector)
        loginPayload.password_selector = definition.login_password_selector;
      if (definition.login_submit_selector)
        loginPayload.submit_selector = definition.login_submit_selector;

      const baseCfg = {
        ...normalizeForSave(config),
        ...(definition.locale != null && definition.locale !== '' && { locale: definition.locale }),
        ...(definition.country != null &&
          definition.country !== '' && { country: definition.country }),
        ...(definition.currency != null &&
          definition.currency !== '' && { currency: definition.currency }),
        ...(Object.keys(loginPayload).length > 0 && { login: loginPayload }),
      };

      // For child definitions: only persist overrides so template changes propagate.
      // Keys that match the parent are omitted; the backend merge will use the parent's value.
      const cfgToSave =
        definition.parent_definition_id && parentConfig
          ? (() => {
              const scrapKeys = [
                'use_playwright',
                'two_stage_scraping',
                'use_proxy',
                'search_url_template',
                'rate_limit',
                'delay_between_requests',
                'use_common_headers',
                'use_common_user_agents',
                'additional_headers',
                'additional_user_agents',
                'allowed_image_hosts',
                'capture_images',
                'max_stage2_products',
                'channel',
              ];
              const out: Record<string, any> = {};
              // Only include scraping keys that differ from parent
              for (const k of scrapKeys) {
                const v = (baseCfg as any)[k];
                const pv = (parentConfig as any)[k];
                if (JSON.stringify(v) !== JSON.stringify(pv)) {
                  out[k] = v;
                }
              }
              // Always include pages and seller_navigation if present (they are compared in full)
              if (baseCfg.pages && Object.keys(baseCfg.pages).length > 0) {
                const parentPages = (parentConfig as any).pages || {};
                if (JSON.stringify(baseCfg.pages) !== JSON.stringify(parentPages)) {
                  out.pages = baseCfg.pages;
                }
              }
              if ('seller_navigation' in baseCfg) {
                const pnav = (parentConfig as any).seller_navigation;
                if (JSON.stringify(baseCfg.seller_navigation) !== JSON.stringify(pnav)) {
                  out.seller_navigation = baseCfg.seller_navigation;
                }
              }
              // Always include locale/country/currency and login
              if (baseCfg.locale != null) out.locale = baseCfg.locale;
              if (baseCfg.country != null) out.country = baseCfg.country;
              if (baseCfg.currency != null) out.currency = baseCfg.currency;
              if (baseCfg.login) out.login = baseCfg.login;
              return out;
            })()
          : baseCfg;

      const updatedBy = await getCurrentProviderId();

      let savedDefinitionId = definition.id;
      if (isNew) {
        const result = await createDefinition.mutateAsync({
          name,
          version: definition.version,
          domain,
          is_active: definition.is_active,
          config: cfgToSave,
          ...(updatedBy ? { updated_by: updatedBy } : {}),
          // Inheritance fields
          is_template: definition.is_template,
          parent_definition_id: definition.parent_definition_id,
          // Locale-specific metadata
          country: definition.country,
          locale: definition.locale,
          currency: definition.currency,
          // Login credentials (stored in config.login)
          login_username: definition.login_username ?? undefined,
          login_password: definition.login_password ?? undefined,
        });
        // Get the new ID from the result for navigation
        savedDefinitionId = result?.id || result?.data?.id;
      } else {
        await updateDefinition.mutateAsync({
          id: definition.id,
          definition: {
            name,
            version: definition.version,
            domain,
            is_active: definition.is_active,
            config: cfgToSave,
            // Inheritance fields
            is_template: definition.is_template,
            parent_definition_id: definition.parent_definition_id,
            // Locale-specific metadata
            country: definition.country,
            locale: definition.locale,
            currency: definition.currency,
            // Login credentials (stored in config.login)
            login_username: definition.login_username ?? undefined,
            login_password: definition.login_password ?? undefined,
            ...(updatedBy ? { updated_by: updatedBy } : {}),
          },
        });
      }

      if (shouldNavigate) {
        // Navigate back to the definitions list, preserving all list state params
        const definitionsUrl = '/definitions';
        const queryString = searchParams.toString();
        navigate(`${definitionsUrl}${queryString ? `?${queryString}` : ''}`);
      } else {
        // Stay on the page - if it was a new definition, navigate to the edit URL
        if (isNew && savedDefinitionId) {
          navigate(`/definitions/${savedDefinitionId}`, { replace: true });
          setIsNew(false);
          // Update the definition with the new ID
          setDefinition(prev => ({ ...prev, id: savedDefinitionId }));
        }
        // Refetch to ensure we have the latest data
        refetchDefinition();
      }
    } catch (error) {
      console.error('Error saving definition:', error);
      setConfigError('Failed to save definition. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle save button click based on current mode
  const handleSaveClick = () => {
    handleSave(!saveAndContinue);
  };

  // Handle save menu option selection
  const handleSaveMenuItemClick = (continueEditing: boolean) => {
    // Update preference and save to localStorage
    setSaveAndContinue(continueEditing);
    localStorage.setItem('definitionEditor.saveAndContinue', String(continueEditing));
    setSaveMenuOpen(false);
    // Perform the save with the selected option
    handleSave(!continueEditing);
  };

  const handleSaveMenuToggle = () => {
    setSaveMenuOpen(prevOpen => !prevOpen);
  };

  const handleSaveMenuClose = (event: Event) => {
    if (
      saveMenuAnchorRef.current &&
      saveMenuAnchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }
    setSaveMenuOpen(false);
  };

  const handleClearWorkStatus = () => {
    if (!definition.id || definition.being_worked_by !== currentProviderId) return;
    clearWorkStatus.mutate(
      { id: definition.id, workingBy: null },
      {
        onSuccess: () => {
          setDefinition(prev => ({
            ...prev,
            being_worked_by: null,
            being_worked_at: null,
          }));
        },
      }
    );
  };

  const handleCancel = () => {
    // Preserve all list state params when navigating back
    const definitionsUrl = '/definitions';
    // Pass through all URL params from the editor back to the list
    const queryString = searchParams.toString();
    navigate(`${definitionsUrl}${queryString ? `?${queryString}` : ''}`);
  };

  const handlePasteDefinition = () => {
    setShowPasteDialog(true);
  };

  const handlePasteConfirm = () => {
    try {
      const parsedDefinition = JSON.parse(pasteText);

      // Validate that it looks like a marketplace definition
      if (!parsedDefinition || typeof parsedDefinition !== 'object') {
        throw new Error('Invalid JSON format');
      }

      // Extract definition metadata
      if (parsedDefinition.id) {
        setDefinition(prev => ({
          ...prev,
          id: parsedDefinition.id,
          marketplace: parsedDefinition.marketplace || parsedDefinition.id,
          name: parsedDefinition.name || '',
          version: parsedDefinition.version || '1.0.0',
          domain: parsedDefinition.domain || '',
          is_active: parsedDefinition.is_active !== undefined ? parsedDefinition.is_active : true,
          brand_id: parsedDefinition.brand_id || null,
          product_id: parsedDefinition.product_id || null,
          ai_maintenance_level: parsedDefinition.ai_maintenance_level || 'low',
        }));
      }

      // Extract configuration
      if (parsedDefinition.config) {
        const configData = parsedDefinition.config;
        // Migrate old structure to new if needed
        const useCommonHeaders =
          configData.use_common_headers !== undefined ? configData.use_common_headers : true;
        const useCommonUserAgents =
          configData.use_common_user_agents !== undefined
            ? configData.use_common_user_agents
            : true;
        const additionalHeaders =
          configData.additional_headers ||
          (configData.headers && !useCommonHeaders ? configData.headers : {});
        const additionalUserAgents =
          configData.additional_user_agents ||
          (configData.user_agents && !useCommonUserAgents ? configData.user_agents : []);

        setConfig({
          pages: normalizePagesForEditor(configData.pages || {}),
          use_common_headers: useCommonHeaders,
          use_common_user_agents: useCommonUserAgents,
          additional_headers: additionalHeaders,
          additional_user_agents: additionalUserAgents,
          use_playwright: configData.use_playwright || false,
          search_url_template: configData.search_url_template || '',
          two_stage_scraping: configData.two_stage_scraping || false,
          rate_limit: configData.rate_limit || 5,
          delay_between_requests: configData.delay_between_requests || 1.0,
          allowed_image_hosts: configData.allowed_image_hosts || [],
          locale: configData.locale || parsedDefinition.locale || '',
          // Seller navigation (either nested under config or at the root of the definition)
          seller_navigation:
            configData.seller_navigation || parsedDefinition.seller_navigation || undefined,
        });
        if (configData.login) {
          setDefinition(prev => ({
            ...prev,
            login_username: configData.login.username ?? prev.login_username,
            login_password: configData.login.password ?? prev.login_password,
            login_username_selector:
              configData.login.username_selector ?? prev.login_username_selector,
            login_password_selector:
              configData.login.password_selector ?? prev.login_password_selector,
            login_submit_selector: configData.login.submit_selector ?? prev.login_submit_selector,
          }));
        }
      } else if (parsedDefinition.pages) {
        // If config is at root level
        const useCommonHeaders =
          parsedDefinition.use_common_headers !== undefined
            ? parsedDefinition.use_common_headers
            : true;
        const useCommonUserAgents =
          parsedDefinition.use_common_user_agents !== undefined
            ? parsedDefinition.use_common_user_agents
            : true;
        const additionalHeaders =
          parsedDefinition.additional_headers ||
          (parsedDefinition.headers && !useCommonHeaders ? parsedDefinition.headers : {});
        const additionalUserAgents =
          parsedDefinition.additional_user_agents ||
          (parsedDefinition.user_agents && !useCommonUserAgents
            ? parsedDefinition.user_agents
            : []);

        setConfig({
          pages: normalizePagesForEditor(parsedDefinition.pages || {}),
          use_common_headers: useCommonHeaders,
          use_common_user_agents: useCommonUserAgents,
          additional_headers: additionalHeaders,
          additional_user_agents: additionalUserAgents,
          use_playwright: parsedDefinition.use_playwright || false,
          search_url_template: parsedDefinition.search_url_template || '',
          two_stage_scraping: parsedDefinition.two_stage_scraping || false,
          rate_limit: parsedDefinition.rate_limit || 5,
          delay_between_requests: parsedDefinition.delay_between_requests || 1.0,
          allowed_image_hosts: parsedDefinition.allowed_image_hosts || [],
          locale: parsedDefinition.locale || '',
          // Seller navigation when configuration is at the root level
          seller_navigation: parsedDefinition.seller_navigation || undefined,
        });
        if (parsedDefinition.login) {
          setDefinition(prev => ({
            ...prev,
            login_username: parsedDefinition.login.username ?? prev.login_username,
            login_password: parsedDefinition.login.password ?? prev.login_password,
            login_username_selector:
              parsedDefinition.login.username_selector ?? prev.login_username_selector,
            login_password_selector:
              parsedDefinition.login.password_selector ?? prev.login_password_selector,
            login_submit_selector:
              parsedDefinition.login.submit_selector ?? prev.login_submit_selector,
          }));
        }
      }

      setShowPasteDialog(false);
      setPasteText('');
      setConfigError('');
    } catch (error) {
      setConfigError(
        `Failed to parse definition: ${error instanceof Error ? error.message : 'Invalid JSON'}`
      );
    }
  };

  const handlePasteCancel = () => {
    setShowPasteDialog(false);
    setPasteText('');
  };

  // Version history functions
  const loadVersionHistory = async () => {
    if (!id || id === 'new') return;

    setIsLoadingVersions(true);
    try {
      const response = await apiClient.get(endpoints.definitionVersions(id));
      setVersions(response.data.versions || []);
    } catch (error) {
      console.error('Failed to load version history:', error);
      setConfigError('Failed to load version history');
    } finally {
      setIsLoadingVersions(false);
    }
  };

  const handleViewVersionHistory = async () => {
    await loadVersionHistory();
    setShowVersionHistory(true);
  };

  const handleRollback = async (versionId: string) => {
    if (
      !id ||
      !window.confirm(
        'Are you sure you want to rollback to this version? This will replace your current configuration.'
      )
    ) {
      return;
    }

    setIsLoading(true);
    try {
      await apiClient.post(endpoints.definitionRollback(id, versionId));

      // Reload the definition
      await refetchDefinition();

      setShowVersionHistory(false);
      setConfigError('');
      alert('Successfully rolled back to the selected version');
    } catch (error) {
      console.error('Failed to rollback:', error);
      setConfigError('Failed to rollback to the selected version');
    } finally {
      setIsLoading(false);
    }
  };

  const addField = (pageType: 'search_results' | 'product_listing' | 'seller_page') => {
    const newField: FieldDefinition = {
      name: '',
      required: false,
      data_type: 'string',
      selectors: [''],
      selector_type: 'css',
      source: 'html', // Default to HTML source
    };

    const newFieldKey = `field_${Date.now()}`;
    setConfig(prev => ({
      ...prev,
      pages: {
        ...prev.pages,
        [pageType]: {
          ...prev.pages[pageType],
          fields: {
            ...prev.pages[pageType]?.fields,
            [newFieldKey]: newField,
          },
        },
      },
    }));

    // Auto-expand the newly added field for seller pages
    if (pageType === 'seller_page') {
      setExpandedSellerFields(prev => new Set(prev).add(newFieldKey));
    }
  };

  const updateField = (
    pageType: 'search_results' | 'product_listing' | 'seller_page',
    fieldKey: string,
    updates: Partial<FieldDefinition>
  ) => {
    setConfig(prev => ({
      ...prev,
      pages: {
        ...prev.pages,
        [pageType]: {
          ...prev.pages[pageType],
          fields: {
            ...prev.pages[pageType]?.fields,
            [fieldKey]: applyFieldUpdates(prev.pages[pageType]?.fields?.[fieldKey], updates),
          },
        },
      },
    }));
  };

  const removeField = (
    pageType: 'search_results' | 'product_listing' | 'seller_page',
    fieldKey: string
  ) => {
    setConfig(prev => {
      const newFields = { ...prev.pages[pageType]?.fields };
      delete newFields[fieldKey];
      return {
        ...prev,
        pages: {
          ...prev.pages,
          [pageType]: {
            ...prev.pages[pageType],
            fields: newFields,
          },
        },
      };
    });
  };

  const addSelector = (
    pageType: 'search_results' | 'product_listing' | 'seller_page',
    fieldKey: string
  ) => {
    setConfig(prev => ({
      ...prev,
      pages: {
        ...prev.pages,
        [pageType]: {
          ...prev.pages[pageType],
          fields: {
            ...prev.pages[pageType]?.fields,
            [fieldKey]: {
              ...prev.pages[pageType]?.fields?.[fieldKey],
              selectors: [...(prev.pages[pageType]?.fields?.[fieldKey]?.selectors || []), ''],
            },
          },
        },
      },
    }));
  };

  const updateSelector = (
    pageType: 'search_results' | 'product_listing' | 'seller_page',
    fieldKey: string,
    selectorIndex: number,
    value: string
  ) => {
    setConfig(prev => {
      const newSelectors = [...(prev.pages[pageType]?.fields?.[fieldKey]?.selectors || [])];
      newSelectors[selectorIndex] = value;
      return {
        ...prev,
        pages: {
          ...prev.pages,
          [pageType]: {
            ...prev.pages[pageType],
            fields: {
              ...prev.pages[pageType]?.fields,
              [fieldKey]: {
                ...prev.pages[pageType]?.fields?.[fieldKey],
                selectors: newSelectors,
              },
            },
          },
        },
      };
    });
  };

  const removeSelector = (
    pageType: 'search_results' | 'product_listing' | 'seller_page',
    fieldKey: string,
    selectorIndex: number
  ) => {
    setConfig(prev => {
      const newSelectors = [...(prev.pages[pageType]?.fields?.[fieldKey]?.selectors || [])];
      newSelectors.splice(selectorIndex, 1);
      return {
        ...prev,
        pages: {
          ...prev.pages,
          [pageType]: {
            ...prev.pages[pageType],
            fields: {
              ...prev.pages[pageType]?.fields,
              [fieldKey]: {
                ...prev.pages[pageType]?.fields?.[fieldKey],
                selectors: newSelectors,
              },
            },
          },
        },
      };
    });
  };

  const updatePostProcessing = (
    pageType: 'search_results' | 'product_listing' | 'seller_page',
    fieldKey: string,
    updates: Partial<PostProcessingConfig>
  ) => {
    setConfig(prev => ({
      ...prev,
      pages: {
        ...prev.pages,
        [pageType]: {
          ...prev.pages[pageType],
          fields: {
            ...prev.pages[pageType]?.fields,
            [fieldKey]: {
              ...prev.pages[pageType]?.fields?.[fieldKey],
              post_processing: {
                ...prev.pages[pageType]?.fields?.[fieldKey]?.post_processing,
                ...updates,
              },
            },
          },
        },
      },
    }));
  };

  /** Options UI for canonicalize_url post-processing (shared across page editors). */
  const renderCanonicalizeUrlOptions = (
    postProcessing: PostProcessingConfig,
    onUpdate: (updates: Partial<PostProcessingConfig>) => void
  ) => (
    <>
      <Grid item xs={12} md={6}>
        <FormControl fullWidth size="small">
          <InputLabel>Trailing Slash</InputLabel>
          <Select
            value={postProcessing.trailing_slash || 'strip'}
            label="Trailing Slash"
            onChange={e =>
              onUpdate({
                trailing_slash: e.target.value as 'strip' | 'preserve',
              })
            }
          >
            <MenuItem value="strip">Strip (remove trailing /)</MenuItem>
            <MenuItem value="preserve">Preserve</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={postProcessing.strip_www !== false}
              onChange={e => onUpdate({ strip_www: e.target.checked })}
            />
          }
          label="Strip www. from host"
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={postProcessing.drop_fragment !== false}
              onChange={e => onUpdate({ drop_fragment: e.target.checked })}
            />
          }
          label="Drop URL fragment (#...)"
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={postProcessing.lowercase_host !== false}
              onChange={e => onUpdate({ lowercase_host: e.target.checked })}
            />
          }
          label="Lowercase host"
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          size="small"
          label="Keep Query Params (comma-separated)"
          placeholder="e.g. skuId, fbid"
          value={(postProcessing.keep_query_params || []).join(', ')}
          onChange={e => {
            const parsed = e.target.value
              .split(',')
              .map(p => p.trim())
              .filter(Boolean);
            onUpdate({ keep_query_params: parsed.length ? parsed : undefined });
          }}
          helperText="Leave empty to preserve the full query string (default)."
        />
      </Grid>
      <Grid item xs={12}>
        <Typography variant="caption" color="text.secondary">
          Canonicalize URL normalizes host/path for dedup. Default strips trailing slashes (except a
          lone &quot;/&quot;).
        </Typography>
      </Grid>
    </>
  );

  const removePostProcessing = (
    pageType: 'search_results' | 'product_listing' | 'seller_page',
    fieldKey: string
  ) => {
    setConfig(prev => ({
      ...prev,
      pages: {
        ...prev.pages,
        [pageType]: {
          ...prev.pages[pageType],
          fields: {
            ...prev.pages[pageType]?.fields,
            [fieldKey]: {
              ...prev.pages[pageType]?.fields?.[fieldKey],
              post_processing: undefined,
            },
          },
        },
      },
    }));
  };

  // Compute used field keys (post-migration the canonical key is the name)
  const usedFieldNames = (page: PageKind) => {
    const fields: Record<string, any> = ((config.pages as any)?.[page]?.fields || {}) as any;
    return new Set(
      Object.entries(fields)
        .map(([k, v]) => String((v && v.name) || k).trim())
        .filter(Boolean)
    );
  };

  // Rename a field key within a page, preserving its config
  const renameFieldKey = (
    pageType: 'search_results' | 'product_listing' | 'seller_page',
    oldKey: string,
    newKey: string,
    extraUpdates?: Partial<FieldDefinition>
  ) => {
    if (!newKey || oldKey === newKey) return;
    setConfig(prev => {
      const page = prev.pages[pageType] || ({ fields: {} } as any);
      const fields = { ...(page.fields || {}) } as Record<string, any>;
      const current = { ...(fields[oldKey] || {}) } as any;
      delete fields[oldKey];
      // Remove legacy name property; key is canonical after migration
      if ('name' in current) delete current.name;
      fields[newKey] = { ...current, ...(extraUpdates || {}) };
      return {
        ...prev,
        pages: {
          ...prev.pages,
          [pageType]: {
            ...page,
            fields,
          },
        },
      };
    });
  };

  const addUserAgent = () => {
    setConfig(prev => ({
      ...prev,
      additional_user_agents: [...(prev.additional_user_agents || []), ''],
    }));
  };

  const updateUserAgent = (index: number, value: string) => {
    setConfig(prev => ({
      ...prev,
      additional_user_agents:
        prev.additional_user_agents?.map((agent, i) => (i === index ? value : agent)) || [],
    }));
  };

  const removeUserAgent = (index: number) => {
    setConfig(prev => ({
      ...prev,
      additional_user_agents: prev.additional_user_agents?.filter((_, i) => i !== index) || [],
    }));
  };

  const addHeader = () => {
    setConfig(prev => ({
      ...prev,
      additional_headers: { ...(prev.additional_headers || {}), '': '' },
    }));
  };

  const updateHeader = (key: string, value: string) => {
    setConfig(prev => {
      const newHeaders = { ...(prev.additional_headers || {}) };
      if (key && value) {
        newHeaders[key] = value;
      } else if (key) {
        delete newHeaders[key];
      }
      return { ...prev, additional_headers: newHeaders };
    });
  };

  const removeHeader = (key: string) => {
    setConfig(prev => {
      const newHeaders = { ...(prev.additional_headers || {}) };
      delete newHeaders[key];
      return { ...prev, additional_headers: newHeaders };
    });
  };

  // Note: Common headers and user agents are now managed in Settings page

  const addCommonSearchResultFields = () => {
    const commonFields = {
      title: {
        name: 'title',
        required: true,
        data_type: 'string',
        selectors: ['h2 a span', '.s-title-instructions-style h2 a span', 'h2 a'],
        selector_type: 'css',
        post_processing: {
          type: 'strip' as const,
          chars: ' \t\n\r',
        },
      },
      url: {
        name: 'url',
        required: true,
        data_type: 'url',
        selectors: ['h2 a', 'a[href*="/dp/"]'],
        selector_type: 'css',
        attribute: 'href',
        post_processing: {
          type: 'python' as const,
          code: 'url.match(/^(.*?\\/dp\\/[A-Z0-9]+).*$/)?.[1] || url',
        },
      },
      price: {
        name: 'price',
        required: false,
        data_type: 'decimal',
        selectors: ['.a-price .a-offscreen', '.a-price-whole', '.a-price-range .a-offscreen'],
        selector_type: 'css',
        post_processing: {
          type: 'remove_currency' as const,
        },
      },
      image: {
        name: 'image',
        required: false,
        data_type: 'url',
        selectors: ['.s-image', 'img[src*="images"]'],
        selector_type: 'css',
        attribute: 'src',
      },
    };

    setConfig(prev => {
      const existingFields = { ...(prev.pages?.search_results?.fields || {}) };
      const newFields = { ...existingFields };

      // Only add fields that don't already exist
      Object.entries(commonFields).forEach(([key, field]) => {
        if (!existingFields[key]) {
          newFields[key] = field;
        }
      });

      return {
        ...prev,
        pages: {
          ...prev.pages,
          search_results: {
            ...prev.pages?.search_results,
            page_type: 'search_results',
            fields: newFields,
          },
        },
      };
    });
  };

  const addCommonProductListingFields = () => {
    const commonFields = {
      title: {
        name: 'title',
        required: true,
        data_type: 'string',
        selectors: ['#productTitle', 'h1', '.product-title'],
        selector_type: 'css',
        post_processing: {
          type: 'strip' as const,
          chars: ' \t\n\r',
        },
      },
      price: {
        name: 'price',
        required: false,
        data_type: 'decimal',
        selectors: ['#priceblock_ourprice', '#priceblock_dealprice', '.a-price .a-offscreen'],
        selector_type: 'css',
        post_processing: {
          type: 'remove_currency' as const,
        },
      },
      description: {
        name: 'description',
        required: false,
        data_type: 'string',
        selectors: ['#productDescription p', '#feature-bullets li', '.product-description'],
        selector_type: 'css',
        post_processing: {
          type: 'strip' as const,
          chars: ' \t\n\r',
        },
      },
      brand: {
        name: 'brand',
        required: false,
        data_type: 'string',
        selectors: ['#bylineInfo', '.brand', '.product-brand'],
        selector_type: 'css',
        post_processing: {
          type: 'strip' as const,
          chars: ' \t\n\r',
        },
      },
      itemNumber: {
        name: 'itemNumber',
        required: false,
        data_type: 'string',
        selectors: ['body'],
        selector_type: 'css',
        source: 'url' as const,
        post_processing: {
          type: 'python' as const,
          code: 'var match = url.match(/\\/dp\\/([A-Z0-9]+)/); if (match && match[1]) { return match[1]; } else { return null; }',
        },
      },
      relatedItems: {
        name: 'relatedItems',
        required: false,
        data_type: 'string_list',
        selectors: [
          'li[data-defaultasin]',
          "#twister a[href*='/dp/']",
          "#variation_twister a[href*='/dp/']",
        ],
        selector_type: 'css',
      },
      availability: {
        name: 'availability',
        required: false,
        data_type: 'string',
        selectors: ['#availability', '.stock-status', '.availability'],
        selector_type: 'css',
        post_processing: {
          type: 'strip' as const,
          chars: ' \t\n\r',
        },
      },
      number_sold: {
        name: 'number_sold',
        required: false,
        data_type: 'decimal',
        selectors: ['[data-testid*="sold"]', '.items-sold', '.qty-sold', '.sold-qty'],
        selector_type: 'css',
        post_processing: {
          type: 'regex_replace' as const,
          pattern: '[^0-9]+',
          replacement: '',
        },
      },
    };

    setConfig(prev => {
      const existingFields = { ...(prev.pages?.product_listing?.fields || {}) };
      const newFields = { ...existingFields };

      // Only add fields that don't already exist
      Object.entries(commonFields).forEach(([key, field]) => {
        if (!existingFields[key]) {
          newFields[key] = field;
        }
      });

      return {
        ...prev,
        pages: {
          ...prev.pages,
          product_listing: {
            ...prev.pages?.product_listing,
            page_type: 'product_listing',
            fields: newFields,
          },
        },
      };
    });
  };

  const performAiValidation = async () => {
    if (!definition.domain) {
      setValidationError('Please set a domain before running AI validation.');
      setAiAccordionExpanded(true);
      return;
    }

    // Create abort controller for cancellation
    const abortController = new AbortController();
    setValidationAbortController(abortController);

    setIsAiValidating(true);
    setValidationProgress(0);
    setValidationStep('Initializing');
    setValidationSummary(null);
    setValidationSuggestions(null);
    setValidationError(null);
    setAppliedSuggestions(new Set());
    setDismissedSuggestions(new Set());
    setAiAccordionExpanded(true); // Open accordion when validation starts

    try {
      // Call the real AI validation service. As of MR 2, the server runs
      // entirely on suggestions — never mutates the definition. We collect
      // approve/dismiss decisions per suggestion below and apply them to
      // local config state only when the user explicitly approves.
      const finalResponse = await aiValidationService.validateDefinition(
        { ...definition, config } as any,
        {
          onProgress: status => {
            setValidationProgress(status.progress || 0);
            setValidationStep(status.current_step || '');
            if (status.validation_id) {
              setCurrentValidationId(status.validation_id);
            }
          },
          abortSignal: abortController.signal,
        }
      );

      // Persist summary and suggestions for UI display.
      setValidationSummary(finalResponse.analysis_summary || null);
      setValidationSuggestions(finalResponse.suggestions || []);

      // Flag every field referenced by a suggestion so the form rows render
      // an "AI suggestion pending" marker. This is the only source for the
      // enhanced-field set now that the auto-apply diff has been removed
      // (improved_definition is identical to current as of MR 2).
      const fieldsWithSuggestions = new Set<string>();
      (finalResponse.suggestions || []).forEach((s: ValidationSuggestion) => {
        if (s.page && s.field) {
          fieldsWithSuggestions.add(`${s.page}.fields.${s.field}`);
        }
      });
      setEnhancedFieldSet(fieldsWithSuggestions);
      console.debug('[AI-UI] Fields with pending suggestions:', Array.from(fieldsWithSuggestions));

      // Note: the previous build refetched the server-side definition here
      // to pick up auto-applied changes. That refetch is removed because
      // the server no longer mutates definitions during validation. The
      // user reviews each suggestion in the panel below and clicks Approve
      // to apply it to the local config; nothing persists until Save.
    } catch (error) {
      console.error('AI validation failed:', error);
      const errorMsg =
        error instanceof Error ? error.message : 'AI validation failed. Please try again.';
      setValidationError(errorMsg);
      // Accordion stays expanded so user can see the error
    } finally {
      setIsAiValidating(false);
      setValidationAbortController(null);
      setCurrentValidationId(null);
    }
  };

  // MR 3: approve/reject handlers for individual AI suggestions.
  //
  // applySuggestion mutates the local `config` state with the suggested
  // change. The change isn't persisted until the user clicks Save on the
  // editor — the same mental model as every other field edit in this form.
  // dismissSuggestion just hides the suggestion from the panel without
  // touching config.
  const applySuggestion = (idx: number, s: ValidationSuggestion) => {
    if (appliedSuggestions.has(idx) || dismissedSuggestions.has(idx)) return;

    // Only suggestions that target a specific page + field with a concrete
    // suggested_selector are auto-applyable. Everything else (general
    // config warnings, performance notes, etc.) is advisory and gets
    // dismissed when the user clicks Approve.
    if (s.page && s.field && s.suggested_selector) {
      setConfig(prev => {
        const next = { ...prev } as any;
        next.pages = { ...(next.pages || {}) };
        const pageKey = s.page as 'search_results' | 'product_listing' | 'seller_page';
        next.pages[pageKey] = { ...(next.pages[pageKey] || {}) };
        next.pages[pageKey].fields = { ...((next.pages[pageKey] as any).fields || {}) };
        const fieldKey = s.field as string;
        const existing = next.pages[pageKey].fields[fieldKey] || {};
        const existingSelectors: string[] = Array.isArray(existing.selectors)
          ? existing.selectors
          : [];
        // Prepend the suggested selector so it takes priority. Preserve
        // existing fallbacks unless they're the broken current_selector.
        const filtered = existingSelectors.filter(
          x => !s.current_selector || x !== s.current_selector
        );
        next.pages[pageKey].fields[fieldKey] = {
          ...existing,
          selectors: Array.from(new Set([s.suggested_selector, ...filtered])),
          selector_type: existing.selector_type || 'css',
        };
        return next;
      });
    }

    setAppliedSuggestions(prev => {
      const next = new Set(prev);
      next.add(idx);
      return next;
    });
  };

  const dismissSuggestion = (idx: number) => {
    setDismissedSuggestions(prev => {
      const next = new Set(prev);
      next.add(idx);
      return next;
    });
  };

  const cancelAiValidation = async () => {
    if (validationAbortController) {
      // Cancel frontend polling
      validationAbortController.abort();

      // Cancel backend validation if we have a validation ID
      if (currentValidationId) {
        try {
          await aiValidationService.cancelValidation(currentValidationId);
          console.log(`Backend validation ${currentValidationId} cancelled successfully`);
        } catch (error) {
          console.error('Failed to cancel backend validation:', error);
          // Still show cancellation message even if backend cancel fails
        }
      }

      setValidationError('Validation cancelled by user');
      setIsAiValidating(false);
      setValidationAbortController(null);
      setCurrentValidationId(null);
    }
  };

  // Helper to check if test URLs are configured for AI validation
  const hasTestUrls = (): boolean => {
    const pages = config?.pages || {};
    const searchUrls = pages.search_results?.test_urls || [];
    const productUrls = pages.product_listing?.test_urls || [];
    const sellerUrls = pages.seller_page?.test_urls || [];

    // At least one page type should have test URLs
    return (
      searchUrls.filter((url: string) => url && url.trim()).length > 0 ||
      productUrls.filter((url: string) => url && url.trim()).length > 0 ||
      sellerUrls.filter((url: string) => url && url.trim()).length > 0
    );
  };

  // (applyAiSuggestions removed - no longer used)

  // Show loading state while fetching definition
  if (isLoadingDefinition && !isNew) {
    return (
      <Box sx={{ width: '100%' }}>
        <Typography>Loading definition...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h4">
            {isNew ? 'New Marketplace Definition' : 'Edit Marketplace Definition'}
          </Typography>
          <Tooltip title="View documentation guide for marketplace definitions">
            <IconButton
              size="small"
              color="primary"
              href="https://gitlab.com/sipi-ip/scraper_ui/-/blob/main/MARKETPLACE_DEFINITION_GUIDE.md"
              target="_blank"
              component="a"
            >
              <HelpIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            alignItems: 'center',
            flexWrap: 'wrap',
            justifyContent: 'flex-end',
          }}
        >
          {!isNew && definition.being_worked_by && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 1 }}>
              <Chip
                size="small"
                color="info"
                label={
                  definition.being_worked_by === currentProviderId
                    ? 'Working (you)'
                    : 'Being worked'
                }
                variant={definition.being_worked_by === currentProviderId ? 'filled' : 'outlined'}
              />
              <UserProviderChip
                providerId={definition.being_worked_by}
                displayNames={userNames}
                namesLoading={userNamesLoading}
              />
              {definition.being_worked_by === currentProviderId && (
                <Tooltip title="Saving keeps this definition marked as being worked">
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleClearWorkStatus}
                      disabled={clearWorkStatus.isLoading}
                    >
                      Clear work status
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Box>
          )}
          {!isNew && (
            <>
              <Button
                variant="outlined"
                startIcon={<InfoIcon />}
                onClick={e => setStatsAnchorEl(e.currentTarget)}
                color="primary"
              >
                Stats
              </Button>
              <Popover
                open={statsPopoverOpen}
                anchorEl={statsAnchorEl}
                onClose={() => setStatsAnchorEl(null)}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
              >
                <Box sx={{ p: 2, minWidth: 280 }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Definition Stats
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary">
                        Overall Success Rate
                      </Typography>
                      <Typography variant="h6">
                        {Number.isFinite(definition.overall_success_rate)
                          ? `${Math.round(definition.overall_success_rate * 100)}%`
                          : 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary">
                        Last Tested
                      </Typography>
                      <Typography variant="body1">
                        {formatDate(definition.last_tested) || 'Never'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary">
                        Created
                      </Typography>
                      <Typography variant="body1">{formatDate(definition.created_at)}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary">
                        Updated
                      </Typography>
                      <Typography variant="body1">{formatDate(definition.updated_at)}</Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Popover>
              <Button
                variant="outlined"
                startIcon={<HistoryIcon />}
                onClick={handleViewVersionHistory}
                color="primary"
              >
                Version History
              </Button>
            </>
          )}
          <Button variant="outlined" startIcon={<CancelIcon />} onClick={handleCancel}>
            Cancel
          </Button>
          <ButtonGroup variant="contained" ref={saveMenuAnchorRef}>
            <Button startIcon={<SaveIcon />} onClick={handleSaveClick} disabled={isLoading}>
              {saveAndContinue ? 'Save & Continue' : 'Save'}
            </Button>
            <Button size="small" onClick={handleSaveMenuToggle} disabled={isLoading}>
              <ArrowDropDownIcon />
            </Button>
          </ButtonGroup>
          <Popper
            sx={{ zIndex: 1300 }}
            open={saveMenuOpen}
            anchorEl={saveMenuAnchorRef.current}
            role={undefined}
            transition
            disablePortal
          >
            {({ TransitionProps, placement }) => (
              <Grow
                {...TransitionProps}
                style={{
                  transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
                }}
              >
                <Paper>
                  <ClickAwayListener onClickAway={handleSaveMenuClose}>
                    <MenuList autoFocusItem>
                      <MenuItem
                        selected={!saveAndContinue}
                        onClick={() => handleSaveMenuItemClick(false)}
                      >
                        Save
                      </MenuItem>
                      <MenuItem
                        selected={saveAndContinue}
                        onClick={() => handleSaveMenuItemClick(true)}
                      >
                        Save & Continue Editing
                      </MenuItem>
                    </MenuList>
                  </ClickAwayListener>
                </Paper>
              </Grow>
            )}
          </Popper>
        </Box>
      </Box>

      {saveFieldError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {saveFieldError}
        </Alert>
      )}

      {configError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={handleRemoveInvalidFields}>
              Remove Invalid Fields
            </Button>
          }
        >
          {configError}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Basic Information */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Basic Information
              </Typography>

              <Grid container spacing={2}>
                {/* Removed Brand and Product selects from definition editor */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <TextField
                      sx={{ flex: 1 }}
                      label="Name"
                      value={definition.name}
                      error={saveFieldError.toLowerCase().includes('name')}
                      onChange={e => {
                        setSaveFieldError('');
                        setDefinition(prev => ({
                          ...prev,
                          name: e.target.value,
                        }));
                      }}
                      required
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={definition.is_active}
                          onChange={e =>
                            setDefinition(prev => ({
                              ...prev,
                              is_active: e.target.checked,
                            }))
                          }
                        />
                      }
                      label="Active"
                    />
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Domain"
                    value={definition.domain}
                    onChange={e => {
                      setSaveFieldError('');
                      setDefinition(prev => ({
                        ...prev,
                        domain: e.target.value,
                      }));
                    }}
                    onBlur={() => {
                      if (!definition.is_template || !definition.domain.trim()) {
                        return;
                      }
                      const prefixed = toTemplateDomain(definition.domain);
                      if (prefixed !== definition.domain) {
                        setDefinition(prev => ({ ...prev, domain: prefixed }));
                      }
                    }}
                    error={saveFieldError.toLowerCase().includes('domain')}
                    helperText={
                      saveFieldError.toLowerCase().includes('domain')
                        ? saveFieldError
                        : definition.is_template
                          ? 'Templates use template.{host} so they are not matched as live sites'
                          : 'Enter the exact domain (e.g., amazon.com or www.amazon.com if www. is required)'
                    }
                    required
                    InputProps={{
                      endAdornment: definition.domain ? (
                        <InputAdornment position="end">
                          <Tooltip title="Open domain in new tab">
                            <IconButton
                              size="small"
                              onClick={() => {
                                const domain = definition.domain.trim();
                                if (domain) {
                                  const url = domain.startsWith('http')
                                    ? domain
                                    : `https://${domain}`;
                                  window.open(url, '_blank', 'noopener,noreferrer');
                                }
                              }}
                              edge="end"
                            >
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      ) : null,
                    }}
                  />
                </Grid>
                {/* Login: selectors on template (inherited by locales), credentials per locale */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, color: 'text.secondary' }}>
                    {definition.is_template
                      ? 'Login selectors (shared by all locales)'
                      : definition.parent_definition_id
                        ? 'Login credentials (per locale)'
                        : 'Login (marketplaces that require auth)'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Login username"
                    value={definition.login_username ?? ''}
                    onChange={e =>
                      setDefinition(prev => ({
                        ...prev,
                        login_username: e.target.value || undefined,
                      }))
                    }
                    helperText={
                      definition.is_template
                        ? 'Optional test credentials for template'
                        : definition.parent_definition_id
                          ? 'Username/email for this locale (e.g. Shopee Indonesia)'
                          : 'Username/email for marketplace login (used with Playwright)'
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="password"
                    label="Login password"
                    value={definition.login_password ?? ''}
                    onChange={e =>
                      setDefinition(prev => ({
                        ...prev,
                        login_password: e.target.value || undefined,
                      }))
                    }
                    helperText={
                      definition.is_template
                        ? 'Optional; locales set their own credentials'
                        : definition.parent_definition_id
                          ? 'Password for this locale'
                          : 'Password for marketplace login'
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mb: 0.5 }}
                  >
                    {definition.is_template
                      ? "Login form selectors — set here and they're inherited by all locale definitions (e.g. Shopee: input[name='loginKey'])"
                      : definition.parent_definition_id
                        ? 'Override selectors only if this locale differs from the template; otherwise inherits from template'
                        : "Optional CSS selectors when auto-detection fails (e.g. Shopee: input[name='loginKey'])"}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Login username selector"
                    value={definition.login_username_selector ?? ''}
                    onChange={e =>
                      setDefinition(prev => ({
                        ...prev,
                        login_username_selector: e.target.value || undefined,
                      }))
                    }
                    placeholder="e.g. input[name='loginKey']"
                    InputProps={getSelectorInputProps(definition.login_username_selector ?? '')}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Login password selector"
                    value={definition.login_password_selector ?? ''}
                    onChange={e =>
                      setDefinition(prev => ({
                        ...prev,
                        login_password_selector: e.target.value || undefined,
                      }))
                    }
                    placeholder="e.g. input[type='password']"
                    InputProps={getSelectorInputProps(definition.login_password_selector ?? '')}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Login submit selector"
                    value={definition.login_submit_selector ?? ''}
                    onChange={e =>
                      setDefinition(prev => ({
                        ...prev,
                        login_submit_selector: e.target.value || undefined,
                      }))
                    }
                    placeholder="e.g. button[type='submit']"
                    InputProps={getSelectorInputProps(definition.login_submit_selector ?? '')}
                  />
                </Grid>
                {/* Inheritance Configuration */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, color: 'text.secondary' }}>
                    Inheritance Settings
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={definition.is_template || false}
                        onChange={e => {
                          const checked = e.target.checked;
                          setDefinition(prev => ({
                            ...prev,
                            is_template: checked,
                            parent_definition_id: checked ? null : prev.parent_definition_id,
                            domain: checked
                              ? toTemplateDomain(prev.domain)
                              : fromTemplateDomain(prev.domain) || prev.domain,
                          }));
                        }}
                      />
                    }
                    label="Template Definition"
                  />
                  <Typography variant="caption" color="textSecondary" display="block">
                    Template definitions serve as parent configurations for locale-specific
                    definitions. Templates cannot be selected for scraping tasks directly. Enabling
                    this prefixes the domain with template. so the extension will not treat the
                    template as a live site.
                  </Typography>
                </Grid>

                {!definition.is_template && (
                  <>
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Inherit From Template</InputLabel>
                          <Select
                            value={definition.parent_definition_id || ''}
                            label="Inherit From Template"
                            onChange={e => {
                              const parentId = e.target.value || null;
                              const template = templates.find(t => t.id === parentId);
                              setDefinition(prev => ({
                                ...prev,
                                parent_definition_id: parentId,
                                domain:
                                  prev.domain.trim() || !template
                                    ? prev.domain
                                    : fromTemplateDomain(template.domain),
                              }));
                            }}
                          >
                            <MenuItem value="">
                              <em>None (Standalone Definition)</em>
                            </MenuItem>
                            {templates
                              .filter(t => t.id !== definition.id) // Don't allow self-reference
                              .map(template => (
                                <MenuItem key={template.id} value={template.id}>
                                  {template.name} ({template.domain})
                                </MenuItem>
                              ))}
                          </Select>
                        </FormControl>
                        {definition.parent_definition_id && (
                          <Tooltip title="Open parent template">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() =>
                                navigate(`/definitions/${definition.parent_definition_id}/edit`)
                              }
                              sx={{ mt: 0.5 }}
                            >
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        display="block"
                        sx={{ mt: 0.5 }}
                      >
                        Child definitions inherit page configurations from their parent template.
                        Override specific fields as needed.
                      </Typography>
                    </Grid>

                    {/* Locale-specific metadata (shown when inheriting) */}
                    {definition.parent_definition_id && (
                      <>
                        <Grid item xs={12} md={4}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Country Code</InputLabel>
                            <Select
                              value={definition.country || ''}
                              onChange={e =>
                                setDefinition(prev => ({
                                  ...prev,
                                  country: e.target.value as string,
                                }))
                              }
                              label="Country Code"
                            >
                              <MenuItem value="">
                                <em>None</em>
                              </MenuItem>
                              {countryCodes.map(c => (
                                <MenuItem key={c.code} value={c.code}>
                                  {c.code} - {c.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            label="Locale"
                            value={definition.locale || ''}
                            onChange={e =>
                              setDefinition(prev => ({
                                ...prev,
                                locale: e.target.value,
                              }))
                            }
                            placeholder="e.g., de-DE, fr-FR"
                            size="small"
                            error={!!getLocaleError(definition.locale)}
                            helperText={getLocaleError(definition.locale)}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Currency</InputLabel>
                            <Select
                              value={definition.currency || ''}
                              onChange={e =>
                                setDefinition(prev => ({
                                  ...prev,
                                  currency: e.target.value as string,
                                }))
                              }
                              label="Currency"
                            >
                              <MenuItem value="">
                                <em>None</em>
                              </MenuItem>
                              {currencyCodes.map(c => (
                                <MenuItem key={c.code} value={c.code}>
                                  {c.code} - {c.name}
                                </MenuItem>
                              ))}
                            </Select>
                            <FormHelperText>
                              Resolved from the Store marketplace (Boarding) for this domain and
                              locale when one exists
                            </FormHelperText>
                          </FormControl>
                        </Grid>
                      </>
                    )}
                  </>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Scraping Configuration */}
        <Grid item xs={12}>
          <Card
            sx={
              definition.parent_definition_id && parentConfig
                ? {
                    border: isScrapingConfigInherited() ? '2px solid #e0e0e0' : '2px solid #ed6c02',
                    backgroundColor: isScrapingConfigInherited()
                      ? 'rgba(0, 0, 0, 0.02)'
                      : 'inherit',
                  }
                : {}
            }
          >
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6">Scraping Configuration</Typography>
                  {definition.parent_definition_id && parentConfig && (
                    <Chip
                      label={isScrapingConfigInherited() ? 'Inherited' : 'Overridden'}
                      size="small"
                      color={isScrapingConfigInherited() ? 'default' : 'warning'}
                      variant={isScrapingConfigInherited() ? 'outlined' : 'filled'}
                    />
                  )}
                </Box>
                {definition.parent_definition_id &&
                  parentConfig &&
                  !isScrapingConfigInherited() && (
                    <Tooltip title="Reset to inherited configuration from parent template">
                      <Button
                        size="small"
                        startIcon={<RestoreIcon />}
                        onClick={() => resetScrapingConfigToInherited()}
                        color="warning"
                      >
                        Reset to Inherited
                      </Button>
                    </Tooltip>
                  )}
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Configure how the scraper fetches and processes pages. These settings affect
                performance, reliability, and the ability to handle different types of websites.
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={config.use_playwright || false}
                        onChange={e =>
                          setConfig(prev => ({
                            ...prev,
                            use_playwright: e.target.checked,
                          }))
                        }
                      />
                    }
                    label="Use Playwright (JavaScript-heavy pages)"
                  />
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{ display: 'block', mt: 0.5 }}
                  >
                    Enable for sites that load content dynamically via JavaScript. Uses a headless
                    browser instead of simple HTTP requests.
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={config.two_stage_scraping || false}
                        onChange={e =>
                          setConfig(prev => ({
                            ...prev,
                            two_stage_scraping: e.target.checked,
                          }))
                        }
                      />
                    }
                    label="Two-Stage Scraping"
                  />
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{ display: 'block', mt: 0.5 }}
                  >
                    First stage collects product URLs from search results, second stage visits each
                    product page for detailed data.
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={config.use_proxy ?? true}
                        onChange={e =>
                          setConfig(prev => ({
                            ...prev,
                            use_proxy: e.target.checked,
                          }))
                        }
                      />
                    }
                    label="Use Proxy"
                  />
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{ display: 'block', mt: 0.5 }}
                  >
                    Enable proxy usage for this marketplace (defaults to global proxy setting if not
                    set)
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Locale"
                    value={config.locale || ''}
                    onChange={e =>
                      setConfig(prev => ({
                        ...prev,
                        locale: e.target.value,
                      }))
                    }
                    placeholder="en-US"
                    helperText="Browser locale (e.g., en-US, id-ID, zh-CN)"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Language Dialog Selectors
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Playwright selectors for language/region dialogs (e.g. Shopee). Tried in order;
                    first match is clicked. Example:
                    button.shopee-button-outline:has-text("English")
                  </Typography>
                  {(config.language_dialog_selectors || []).map((sel: string, idx: number) => (
                    <Box key={idx} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                      <TextField
                        fullWidth
                        size="small"
                        value={sel}
                        onChange={e => {
                          const next = [...(config.language_dialog_selectors || [])];
                          next[idx] = e.target.value;
                          setConfig(prev => ({ ...prev, language_dialog_selectors: next }));
                        }}
                        placeholder='button.shopee-button-outline:has-text("English")'
                      />
                      <Button
                        size="small"
                        color="error"
                        onClick={() => {
                          const next = (config.language_dialog_selectors || []).filter(
                            (_: string, i: number) => i !== idx
                          );
                          setConfig(prev => ({
                            ...prev,
                            language_dialog_selectors: next.length ? next : undefined,
                          }));
                        }}
                      >
                        Remove
                      </Button>
                    </Box>
                  ))}
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() =>
                      setConfig(prev => ({
                        ...prev,
                        language_dialog_selectors: [...(prev.language_dialog_selectors || []), ''],
                      }))
                    }
                  >
                    Add Selector
                  </Button>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    {definition.parent_definition_id && parentConfig ? (
                      <Chip
                        label={
                          isSearchUrlTemplateInherited()
                            ? 'Inherited'
                            : 'Overridden for this locale'
                        }
                        size="small"
                        color={isSearchUrlTemplateInherited() ? 'default' : 'warning'}
                        variant={isSearchUrlTemplateInherited() ? 'outlined' : 'filled'}
                      />
                    ) : (
                      <span />
                    )}
                    {definition.parent_definition_id &&
                      parentConfig &&
                      !isSearchUrlTemplateInherited() && (
                        <Button
                          size="small"
                          startIcon={<RestoreIcon />}
                          onClick={resetSearchUrlTemplateToInherited}
                          color="warning"
                        >
                          Reset to Inherited
                        </Button>
                      )}
                  </Box>
                  <TextField
                    fullWidth
                    label="Search URL Template"
                    value={config.search_url_template || ''}
                    onChange={e =>
                      setConfig(prev => ({
                        ...prev,
                        search_url_template: e.target.value,
                      }))
                    }
                    placeholder="https://{domain}/search?keyword={keywords_slug}"
                    helperText="Locale children can override this without changing selectors. {domain} is the definition domain (www. stripped when used as a subdomain). Mercado Libre: https://listado.{domain}/{keywords_slug} — Brazil: https://lista.{domain}/{keywords_slug}"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    maxRows={12}
                    label="Search Fanout (JSON)"
                    value={
                      (config as any).search_fanout &&
                      ((config as any).search_fanout as any[]).length
                        ? JSON.stringify((config as any).search_fanout, null, 2)
                        : ''
                    }
                    onChange={e => {
                      const txt = e.target.value.trim();
                      if (!txt) {
                        setConfig(prev => {
                          const next: any = { ...prev };
                          delete next.search_fanout;
                          return next;
                        });
                        return;
                      }
                      try {
                        const parsed = JSON.parse(txt);
                        if (Array.isArray(parsed)) {
                          setConfig(prev => ({ ...prev, search_fanout: parsed }) as any);
                        }
                      } catch {
                        /* invalid JSON — leave config untouched until user fixes it */
                      }
                    }}
                    placeholder='[
  {"name": "default",   "params": {"sort": "default"}},
  {"name": "by-sales",  "params": {"sort": "sale-desc"}},
  {"name": "price-low", "params": {"sort": "sale-desc", "filter": "reserve_price[0,99]"}}
]'
                    helperText="Expands one keyword search into N URLs by overlaying each entry's params. Used to break past per-keyword result-set caps (e.g. Taobao's ~440-listing ceiling). Leave empty for no fanout."
                    sx={{ fontFamily: 'monospace' }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Initial Load Delay (ms)"
                    value={config.initial_load_delay_ms ?? ''}
                    onChange={e => {
                      const v = e.target.value;
                      setConfig(prev => ({
                        ...prev,
                        initial_load_delay_ms: v === '' ? undefined : parseInt(v, 10) || 0,
                      }));
                    }}
                    placeholder="0"
                    inputProps={{ min: 0, max: 30000, step: 500 }}
                    helperText="Extra wait after navigation before processing (e.g. 3000 for Shopee loading)"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Rate Limit"
                    value={config.rate_limit || 5}
                    onChange={e =>
                      setConfig(prev => ({
                        ...prev,
                        rate_limit: parseInt(e.target.value) || 5,
                      }))
                    }
                    inputProps={{ min: 1, max: 100 }}
                    helperText="Requests per minute"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Delay Between Requests"
                    value={config.delay_between_requests || 1.0}
                    onChange={e =>
                      setConfig(prev => ({
                        ...prev,
                        delay_between_requests: parseFloat(e.target.value) || 1.0,
                      }))
                    }
                    inputProps={{ min: 0.1, max: 10, step: 0.1 }}
                    helperText="Seconds"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Image Host Allowlist (Global) */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">Allowed Image Hosts (Global)</Typography>
                <Button
                  startIcon={<AddIcon />}
                  size="small"
                  onClick={() =>
                    setConfig(prev => ({
                      ...prev,
                      allowed_image_hosts: [...(prev.allowed_image_hosts || []), ''],
                    }))
                  }
                >
                  Add Host
                </Button>
              </Box>
              {(config.allowed_image_hosts || []).map((host, idx) => (
                <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                  <TextField
                    fullWidth
                    size="small"
                    label={`Host ${idx + 1}`}
                    value={host}
                    onChange={e =>
                      setConfig(prev => ({
                        ...prev,
                        allowed_image_hosts: (prev.allowed_image_hosts || []).map((h, i) =>
                          i === idx ? e.target.value : h
                        ),
                      }))
                    }
                    placeholder="s.alicdn.com"
                    helperText="Only images from these hosts (or their subdomains) will be kept when no page-level list is set."
                  />
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() =>
                      setConfig(prev => ({
                        ...prev,
                        allowed_image_hosts: (prev.allowed_image_hosts || []).filter(
                          (_, i) => i !== idx
                        ),
                      }))
                    }
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>

        {/* Inheritance Status Banner */}
        {definition.parent_definition_id && parentConfig && (
          <Grid item xs={12}>
            <Alert
              severity="info"
              sx={{
                mb: 0,
                '& .MuiAlert-message': { width: '100%' },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                    Inheriting from: {parentDefinitionName}
                  </Typography>
                  <Typography variant="body2">
                    Page configurations below are inherited from the parent template.
                    {hasLocalOverrides()
                      ? ' Some sections have local overrides (shown with orange indicator).'
                      : ' Edit any field to create a local override.'}
                  </Typography>
                </Box>
                {isLoadingParent && <CircularProgress size={20} />}
              </Box>
            </Alert>
          </Grid>
        )}

        {/* Search Results Page Configuration */}
        <Grid item xs={12}>
          <Card
            sx={
              definition.parent_definition_id && parentConfig
                ? {
                    border: isPageConfigInherited('search_results')
                      ? '2px solid #e0e0e0'
                      : '2px solid #ed6c02',
                    backgroundColor: isPageConfigInherited('search_results')
                      ? 'rgba(0, 0, 0, 0.02)'
                      : 'inherit',
                  }
                : {}
            }
          >
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6">Search Results Page Configuration</Typography>
                  {definition.parent_definition_id && parentConfig && (
                    <Chip
                      label={isPageConfigInherited('search_results') ? 'Inherited' : 'Overridden'}
                      size="small"
                      color={isPageConfigInherited('search_results') ? 'default' : 'warning'}
                      variant={isPageConfigInherited('search_results') ? 'outlined' : 'filled'}
                    />
                  )}
                </Box>
                {definition.parent_definition_id &&
                  parentConfig &&
                  !isPageConfigInherited('search_results') && (
                    <Tooltip title="Reset to inherited configuration from parent template">
                      <Button
                        size="small"
                        startIcon={<RestoreIcon />}
                        onClick={() => resetPageToInherited('search_results')}
                        color="warning"
                      >
                        Reset to Inherited
                      </Button>
                    </Tooltip>
                  )}
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Define how to identify and extract data from search results pages. Configure
                selectors to locate product listings and pagination controls.
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="URL Pattern (regex)"
                    value={config.pages?.search_results?.url_pattern || ''}
                    onChange={e =>
                      setConfig(prev => ({
                        ...prev,
                        pages: {
                          ...prev.pages,
                          search_results: {
                            ...prev.pages?.search_results,
                            page_type: 'search_results',
                            url_pattern: e.target.value,
                          },
                        },
                      }))
                    }
                    placeholder=".*/trade/search.*|.*SearchText=.*"
                    helperText="Regex to detect search results URLs"
                    InputProps={getRegexInputProps(
                      config.pages?.search_results?.url_pattern,
                      'Test Search Results URL Pattern'
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Content Container Selector"
                    value={config.pages?.search_results?.content_container || ''}
                    onChange={e =>
                      setConfig(prev => ({
                        ...prev,
                        pages: {
                          ...prev.pages,
                          search_results: {
                            ...prev.pages?.search_results,
                            page_type: 'search_results',
                            content_container: e.target.value,
                          },
                        },
                      }))
                    }
                    placeholder="#mainContent"
                    helperText="Main container for search results"
                    InputProps={getSelectorInputProps(
                      config.pages?.search_results?.content_container
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Item Selector"
                    value={config.pages?.search_results?.item_selector || ''}
                    onChange={e =>
                      setConfig(prev => ({
                        ...prev,
                        pages: {
                          ...prev.pages,
                          search_results: {
                            ...prev.pages?.search_results,
                            page_type: 'search_results',
                            item_selector: e.target.value,
                          },
                        },
                      }))
                    }
                    placeholder="li.s-item"
                    helperText="Selector for individual product items"
                    InputProps={getSelectorInputProps(config.pages?.search_results?.item_selector)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Next Page Selector"
                    value={config.pages?.search_results?.next_page_selector || ''}
                    onChange={e =>
                      setConfig(prev => ({
                        ...prev,
                        pages: {
                          ...prev.pages,
                          search_results: {
                            ...prev.pages?.search_results,
                            page_type: 'search_results',
                            next_page_selector: e.target.value,
                          },
                        },
                      }))
                    }
                    placeholder=".s-pagination-next"
                    helperText="Selector for pagination links"
                    InputProps={getSelectorInputProps(
                      config.pages?.search_results?.next_page_selector
                    )}
                  />
                </Grid>
              </Grid>

              {/* Pagination Fallback — used when next_page_selector and
                  json_next_page_path both come up empty. The pager mutates a
                  query-string parameter on the current URL. See
                  backend/utils/pagination.py. */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Pagination Fallback
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 2, display: 'block' }}
                >
                  Used when next_page_selector and json_next_page_path don't surface a next URL.
                  Mutates a query-string parameter to compute the next page.
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Kind</InputLabel>
                      <Select
                        value={(config.pages?.search_results as any)?.pagination?.kind || ''}
                        label="Kind"
                        onChange={e => {
                          const kind = e.target.value as string;
                          setConfig(prev => {
                            const existing = (prev.pages?.search_results as any)?.pagination || {};
                            const next = kind ? { ...existing, kind } : undefined;
                            return {
                              ...prev,
                              pages: {
                                ...prev.pages,
                                search_results: {
                                  ...prev.pages?.search_results,
                                  page_type: 'search_results',
                                  pagination: next,
                                } as any,
                              },
                            };
                          });
                        }}
                      >
                        <MenuItem value="">
                          <em>None</em>
                        </MenuItem>
                        <MenuItem value="offset_param">offset_param (e.g. Taobao ?s=44)</MenuItem>
                        <MenuItem value="page_param">page_param (e.g. ?page=N)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Param"
                      value={(config.pages?.search_results as any)?.pagination?.param || ''}
                      onChange={e =>
                        setConfig(prev => ({
                          ...prev,
                          pages: {
                            ...prev.pages,
                            search_results: {
                              ...prev.pages?.search_results,
                              page_type: 'search_results',
                              pagination: {
                                ...((prev.pages?.search_results as any)?.pagination || {}),
                                param: e.target.value,
                              },
                            } as any,
                          },
                        }))
                      }
                      placeholder="s"
                      helperText="Query-string parameter to mutate"
                    />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Step"
                      value={(config.pages?.search_results as any)?.pagination?.step ?? ''}
                      onChange={e => {
                        const v = e.target.value === '' ? undefined : parseInt(e.target.value, 10);
                        setConfig(prev => ({
                          ...prev,
                          pages: {
                            ...prev.pages,
                            search_results: {
                              ...prev.pages?.search_results,
                              page_type: 'search_results',
                              pagination: {
                                ...((prev.pages?.search_results as any)?.pagination || {}),
                                step: v,
                              },
                            } as any,
                          },
                        }));
                      }}
                      placeholder="44"
                      helperText="Increment per page"
                    />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Start"
                      value={(config.pages?.search_results as any)?.pagination?.start ?? ''}
                      onChange={e => {
                        const v = e.target.value === '' ? undefined : parseInt(e.target.value, 10);
                        setConfig(prev => ({
                          ...prev,
                          pages: {
                            ...prev.pages,
                            search_results: {
                              ...prev.pages?.search_results,
                              page_type: 'search_results',
                              pagination: {
                                ...((prev.pages?.search_results as any)?.pagination || {}),
                                start: v,
                              },
                            } as any,
                          },
                        }));
                      }}
                      placeholder="0"
                      helperText="Page 1 value (defaults: 0 / 1)"
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Max offset"
                      value={(config.pages?.search_results as any)?.pagination?.max_offset ?? ''}
                      onChange={e => {
                        const v = e.target.value === '' ? undefined : parseInt(e.target.value, 10);
                        setConfig(prev => ({
                          ...prev,
                          pages: {
                            ...prev.pages,
                            search_results: {
                              ...prev.pages?.search_results,
                              page_type: 'search_results',
                              pagination: {
                                ...((prev.pages?.search_results as any)?.pagination || {}),
                                max_offset: v,
                              },
                            } as any,
                          },
                        }));
                      }}
                      placeholder="396"
                      helperText="Stop here (inclusive)"
                    />
                  </Grid>
                </Grid>
              </Box>

              <PageActionsEditor
                pageType="search_results"
                config={config}
                setConfig={setConfig}
                getSelectorInputProps={getSelectorInputProps}
              />

              <JsonIslandsEditor pageType="search_results" config={config} setConfig={setConfig} />

              {/* Fields for Search Results */}
              <Box sx={{ mt: 3 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                  }}
                >
                  <Typography variant="subtitle1">Fields</Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      startIcon={<AddIcon />}
                      onClick={() => addField('search_results')}
                      size="small"
                    >
                      Add Field
                    </Button>
                    <Button
                      startIcon={<AddIcon />}
                      onClick={addCommonSearchResultFields}
                      size="small"
                      variant="outlined"
                    >
                      Add Common Fields
                    </Button>
                  </Box>
                </Box>

                {Object.entries(config.pages?.search_results?.fields || {}).map(
                  ([fieldKey, field]) => {
                    const isInvalid = !isFieldValidForPage(fieldKey, 'search_results');
                    return (
                      <Accordion
                        key={fieldKey}
                        sx={{
                          mb: 1,
                          border: isInvalid ? 2 : 0,
                          borderColor: isInvalid ? 'error.main' : 'transparent',
                          bgcolor: isInvalid ? 'error.light' : 'background.paper',
                        }}
                      >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Box
                            sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}
                          >
                            {isInvalid && (
                              <Tooltip
                                title={`Field '${fieldKey}' is not valid for search_results page type`}
                              >
                                <Chip
                                  label="INVALID"
                                  color="error"
                                  size="small"
                                  sx={{ fontWeight: 'bold' }}
                                />
                              </Tooltip>
                            )}
                            <FormControl size="small" sx={{ minWidth: 220 }}>
                              <InputLabel>Field</InputLabel>
                              <Select
                                value={field.name || fieldKey}
                                label="Field"
                                onChange={e => {
                                  const choice = FIELD_REGISTRY.find(r => r.key === e.target.value);
                                  const newKey = String(e.target.value);
                                  const updates: any = {};
                                  if (choice?.data_type) updates.data_type = choice.data_type;
                                  if (choice?.default_attribute && !field.attribute)
                                    updates.attribute = choice.default_attribute;
                                  renameFieldKey('search_results', fieldKey, newKey, updates);
                                }}
                              >
                                {getRegistryFor('search_results')
                                  .filter(opt => {
                                    const currentKey = String(field.name || fieldKey);
                                    return (
                                      !usedFieldNames('search_results').has(opt.key) ||
                                      opt.key === currentKey
                                    );
                                  })
                                  .map(opt => (
                                    <MenuItem key={opt.key} value={opt.key}>
                                      {opt.label}
                                    </MenuItem>
                                  ))}
                              </Select>
                            </FormControl>
                            <Chip
                              label={field.required ? 'Required' : 'Optional'}
                              color={field.required ? 'error' : 'default'}
                              size="small"
                            />
                            {enhancedFieldSet.has(`search_results.fields.${fieldKey}`) && (
                              <Tooltip title="This field was modified by AI validation">
                                <Chip
                                  icon={<AutoFixHighIcon />}
                                  label="AI Enhanced"
                                  color="secondary"
                                  size="small"
                                  sx={{
                                    fontWeight: 'bold',
                                    animation: 'pulse 2s ease-in-out infinite',
                                    '@keyframes pulse': {
                                      '0%, 100%': { opacity: 1 },
                                      '50%': { opacity: 0.7 },
                                    },
                                  }}
                                />
                              </Tooltip>
                            )}
                            <IconButton
                              onClick={e => {
                                e.stopPropagation();
                                removeField('search_results', fieldKey);
                              }}
                              size="small"
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={3}>
                              <FormControl fullWidth size="small">
                                <InputLabel>Data Type</InputLabel>
                                <Select
                                  value={field.data_type}
                                  label="Data Type"
                                  onChange={e =>
                                    updateField('search_results', fieldKey, {
                                      data_type: e.target.value,
                                    })
                                  }
                                >
                                  <MenuItem value="string">String</MenuItem>
                                  <MenuItem value="decimal">Decimal</MenuItem>
                                  <MenuItem value="url">URL</MenuItem>
                                  <MenuItem value="image">Image</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <FormControl fullWidth size="small">
                                <InputLabel>Selector Type</InputLabel>
                                <Select
                                  value={field.selector_type}
                                  label="Selector Type"
                                  onChange={e =>
                                    updateField('search_results', fieldKey, {
                                      selector_type: e.target.value,
                                    })
                                  }
                                >
                                  <MenuItem value="css">CSS</MenuItem>
                                  <MenuItem value="json_path">JSONPath</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>
                            {!isJsonPathField(field) && (
                              <Grid item xs={12} md={3}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="Attribute"
                                  value={field.attribute || ''}
                                  onChange={e =>
                                    updateField('search_results', fieldKey, {
                                      attribute: e.target.value,
                                    })
                                  }
                                  placeholder="href, src, etc."
                                  helperText="HTML attribute to extract"
                                />
                              </Grid>
                            )}
                            <Grid item xs={12} md={3}>
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={field.required}
                                    onChange={e =>
                                      updateField('search_results', fieldKey, {
                                        required: e.target.checked,
                                      })
                                    }
                                  />
                                }
                                label="Required"
                              />
                            </Grid>

                            {/* Selectors */}
                            <Grid item xs={12}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  mb: 1,
                                }}
                              >
                                <Typography variant="subtitle2">
                                  {isJsonPathField(field) ? 'JSONPath' : 'CSS Selectors'}
                                </Typography>
                                <Button
                                  startIcon={<AddIcon />}
                                  onClick={() => addSelector('search_results', fieldKey)}
                                  size="small"
                                >
                                  Add Selector
                                </Button>
                              </Box>

                              {field.selectors.map((selector, selectorIndex) => (
                                <Box
                                  key={selectorIndex}
                                  sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}
                                >
                                  <TextField
                                    fullWidth
                                    size="small"
                                    label={`Selector ${selectorIndex + 1}`}
                                    value={selector}
                                    onChange={e =>
                                      updateSelector(
                                        'search_results',
                                        fieldKey,
                                        selectorIndex,
                                        e.target.value
                                      )
                                    }
                                    placeholder=".s-item__title"
                                    InputProps={getSelectorInputProps(selector)}
                                  />
                                  <IconButton
                                    onClick={() =>
                                      removeSelector('search_results', fieldKey, selectorIndex)
                                    }
                                    size="small"
                                    color="error"
                                    disabled={field.selectors.length === 1}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Box>
                              ))}
                            </Grid>

                            {/* Post-Processing Configuration */}
                            <Grid item xs={12}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  mb: 1,
                                }}
                              >
                                <Typography variant="subtitle2">Post-Processing</Typography>
                                {!field.post_processing && (
                                  <Button
                                    startIcon={<AddIcon />}
                                    onClick={() =>
                                      updatePostProcessing('search_results', fieldKey, {
                                        type: 'strip',
                                      })
                                    }
                                    size="small"
                                  >
                                    Add Post-Processing
                                  </Button>
                                )}
                                {field.post_processing && (
                                  <Button
                                    onClick={() => removePostProcessing('search_results', fieldKey)}
                                    size="small"
                                    color="error"
                                  >
                                    Remove Post-Processing
                                  </Button>
                                )}
                              </Box>

                              {field.post_processing && (
                                <Box
                                  sx={{
                                    p: 2,
                                    bgcolor: (theme: any) =>
                                      theme.palette.mode === 'dark'
                                        ? 'background.default'
                                        : 'grey.50',
                                    borderRadius: 1,
                                    border: (theme: any) => `1px solid ${theme.palette.divider}`,
                                  }}
                                >
                                  <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                      <FormControl fullWidth size="small">
                                        <InputLabel>Processing Type</InputLabel>
                                        <Select
                                          value={field.post_processing.type}
                                          label="Processing Type"
                                          onChange={e =>
                                            updatePostProcessing('search_results', fieldKey, {
                                              type: e.target.value as any,
                                            })
                                          }
                                        >
                                          <MenuItem value="trim_query_params">
                                            Trim Query Parameters
                                          </MenuItem>
                                          <MenuItem value="canonicalize_url">
                                            Canonicalize URL
                                          </MenuItem>
                                          <MenuItem value="custom">Custom Regex</MenuItem>
                                          <MenuItem value="python">Python</MenuItem>
                                          <MenuItem value="regex_replace">Regex Replace</MenuItem>
                                          <MenuItem value="strip">Strip Characters</MenuItem>
                                          <MenuItem value="remove_currency">
                                            Remove Currency
                                          </MenuItem>
                                          <MenuItem value="parse_quantity_abbrev">
                                            Parse Quantity Abbrev (RB/K/JT)
                                          </MenuItem>
                                          <MenuItem value="cleanse_currency">
                                            Clean Currency
                                          </MenuItem>
                                        </Select>
                                      </FormControl>
                                    </Grid>

                                    {field.post_processing.type === 'custom' && (
                                      <Grid item xs={12} md={6}>
                                        <TextField
                                          fullWidth
                                          size="small"
                                          label="Custom Pattern"
                                          value={field.post_processing.custom_pattern || ''}
                                          onChange={e =>
                                            updatePostProcessing('search_results', fieldKey, {
                                              custom_pattern: e.target.value,
                                            })
                                          }
                                          placeholder="^.*?/(?:dp|gp/product)/([A-Z0-9]{10})(?:/|\\?|#|$)"
                                          helperText="Python re.match on the full value; first capture group is used if present, else the whole match"
                                          InputProps={getRegexInputProps(
                                            field.post_processing.custom_pattern,
                                            'Test custom post-processing regex',
                                            'match',
                                            '',
                                            true
                                          )}
                                        />
                                      </Grid>
                                    )}

                                    {field.post_processing.type === 'canonicalize_url' &&
                                      renderCanonicalizeUrlOptions(field.post_processing, updates =>
                                        updatePostProcessing('search_results', fieldKey, updates)
                                      )}

                                    {field.post_processing.type === 'python' && (
                                      <Grid item xs={12}>
                                        <TextField
                                          fullWidth
                                          multiline
                                          minRows={2}
                                          maxRows={4}
                                          size="small"
                                          label={
                                            field.post_processing.type === 'python'
                                              ? 'Python Code'
                                              : 'JavaScript Code'
                                          }
                                          value={field.post_processing.code || ''}
                                          onChange={e =>
                                            updatePostProcessing('search_results', fieldKey, {
                                              code: e.target.value,
                                            })
                                          }
                                          placeholder="url.match(/^(.*?\\/dp\\/[A-Z0-9]+).*$/)?.[1] || url"
                                          helperText="Code to process the field value. JavaScript: use 'value' variable. Python: use 'value' variable and set 'result' variable."
                                          sx={{
                                            '& .MuiInputBase-root': {
                                              fontFamily:
                                                'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                                              bgcolor: (theme: any) =>
                                                theme.palette.mode === 'dark'
                                                  ? 'background.paper'
                                                  : 'background.default',
                                            },
                                          }}
                                        />
                                      </Grid>
                                    )}

                                    {field.post_processing.type === 'regex_replace' && (
                                      <>
                                        <Grid item xs={12} md={6}>
                                          <TextField
                                            fullWidth
                                            size="small"
                                            label="Pattern"
                                            value={field.post_processing.pattern || ''}
                                            onChange={e =>
                                              updatePostProcessing('search_results', fieldKey, {
                                                pattern: e.target.value,
                                              })
                                            }
                                            placeholder="\\$|USD|€|EUR"
                                            helperText="Regex pattern to find"
                                            InputProps={getRegexInputProps(
                                              field.post_processing.pattern,
                                              'Test regex replacement',
                                              'replace',
                                              field.post_processing.replacement || ''
                                            )}
                                          />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                          <TextField
                                            fullWidth
                                            size="small"
                                            label="Replacement"
                                            value={field.post_processing.replacement || ''}
                                            onChange={e =>
                                              updatePostProcessing('search_results', fieldKey, {
                                                replacement: e.target.value,
                                              })
                                            }
                                            placeholder=""
                                            helperText="Replacement text"
                                          />
                                        </Grid>
                                      </>
                                    )}

                                    {field.post_processing.type === 'strip' && (
                                      <Grid item xs={12} md={6}>
                                        <TextField
                                          fullWidth
                                          size="small"
                                          label="Characters to Strip"
                                          value={field.post_processing.chars || ' \t\n\r'}
                                          onChange={e =>
                                            updatePostProcessing('search_results', fieldKey, {
                                              chars: e.target.value,
                                            })
                                          }
                                          placeholder=" \t\n\r"
                                          helperText="Characters to remove from start/end"
                                        />
                                      </Grid>
                                    )}
                                  </Grid>
                                </Box>
                              )}
                            </Grid>
                          </Grid>
                        </AccordionDetails>
                      </Accordion>
                    );
                  }
                )}
              </Box>

              {/* Test URLs for AI Validation moved to the global section above. */}
            </CardContent>
          </Card>
        </Grid>

        {/* Product Listing Page Configuration */}
        <Grid item xs={12}>
          <Card
            sx={
              definition.parent_definition_id && parentConfig
                ? {
                    border: isPageConfigInherited('product_listing')
                      ? '2px solid #e0e0e0'
                      : '2px solid #ed6c02',
                    backgroundColor: isPageConfigInherited('product_listing')
                      ? 'rgba(0, 0, 0, 0.02)'
                      : 'inherit',
                  }
                : {}
            }
          >
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6">Product Listing Page Configuration</Typography>
                  {definition.parent_definition_id && parentConfig && (
                    <Chip
                      label={isPageConfigInherited('product_listing') ? 'Inherited' : 'Overridden'}
                      size="small"
                      color={isPageConfigInherited('product_listing') ? 'default' : 'warning'}
                      variant={isPageConfigInherited('product_listing') ? 'outlined' : 'filled'}
                    />
                  )}
                </Box>
                {definition.parent_definition_id &&
                  parentConfig &&
                  !isPageConfigInherited('product_listing') && (
                    <Tooltip title="Reset to inherited configuration from parent template">
                      <Button
                        size="small"
                        startIcon={<RestoreIcon />}
                        onClick={() => resetPageToInherited('product_listing')}
                        color="warning"
                      >
                        Reset to Inherited
                      </Button>
                    </Tooltip>
                  )}
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="URL Pattern (regex)"
                    value={config.pages?.product_listing?.url_pattern || ''}
                    onChange={e =>
                      setConfig(prev => ({
                        ...prev,
                        pages: {
                          ...prev.pages,
                          product_listing: {
                            ...prev.pages?.product_listing,
                            page_type: 'product_listing',
                            url_pattern: e.target.value,
                          },
                        },
                      }))
                    }
                    placeholder=".*/dp/.*|.*/product-detail/.*|.*/offer/.*"
                    helperText="Regex to detect product listing URLs"
                    InputProps={getRegexInputProps(
                      config.pages?.product_listing?.url_pattern,
                      'Test Product Listing URL Pattern'
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Content Container Selector"
                    value={config.pages?.product_listing?.content_container || ''}
                    onChange={e =>
                      setConfig(prev => ({
                        ...prev,
                        pages: {
                          ...prev.pages,
                          product_listing: {
                            ...prev.pages?.product_listing,
                            page_type: 'product_listing',
                            content_container: e.target.value,
                          },
                        },
                      }))
                    }
                    placeholder="#CenterPanelInternal"
                    helperText="Main container for product content"
                    InputProps={getSelectorInputProps(
                      config.pages?.product_listing?.content_container
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={config.pages?.product_listing?.capture_images || false}
                        onChange={e =>
                          setConfig(prev => ({
                            ...prev,
                            pages: {
                              ...prev.pages,
                              product_listing: {
                                ...prev.pages?.product_listing,
                                page_type: 'product_listing',
                                capture_images: e.target.checked,
                              },
                            },
                          }))
                        }
                      />
                    }
                    label="Capture Images"
                  />
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{ display: 'block', mt: 0.5 }}
                  >
                    Capture images via canvas and upload to image store to avoid CORS/ORB blocking
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="subtitle2">Allowed Image Hosts (Product Page)</Typography>
                    <Button
                      startIcon={<AddIcon />}
                      size="small"
                      onClick={() =>
                        setConfig(prev => ({
                          ...prev,
                          pages: {
                            ...prev.pages,
                            product_listing: {
                              ...prev.pages?.product_listing,
                              page_type: 'product_listing',
                              allowed_image_hosts: [
                                ...(((prev.pages?.product_listing as any)?.allowed_image_hosts ||
                                  []) as string[]),
                                '',
                              ],
                            } as any,
                          },
                        }))
                      }
                    >
                      Add Host
                    </Button>
                  </Box>
                  {(
                    (((config.pages?.product_listing as any) || {}).allowed_image_hosts ||
                      []) as string[]
                  ).map((host: string, idx: number) => (
                    <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                      <TextField
                        fullWidth
                        size="small"
                        label={`Host ${idx + 1}`}
                        value={host}
                        onChange={e =>
                          setConfig(prev => ({
                            ...prev,
                            pages: {
                              ...prev.pages,
                              product_listing: {
                                ...prev.pages?.product_listing,
                                page_type: 'product_listing',
                                allowed_image_hosts: (
                                  ((prev.pages?.product_listing as any)?.allowed_image_hosts ||
                                    []) as string[]
                                ).map((h, i) => (i === idx ? e.target.value : h)),
                              } as any,
                            },
                          }))
                        }
                        placeholder="s.alicdn.com"
                      />
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() =>
                          setConfig(prev => ({
                            ...prev,
                            pages: {
                              ...prev.pages,
                              product_listing: {
                                ...prev.pages?.product_listing,
                                page_type: 'product_listing',
                                allowed_image_hosts: (
                                  ((prev.pages?.product_listing as any)?.allowed_image_hosts ||
                                    []) as string[]
                                ).filter((_, i) => i !== idx),
                              } as any,
                            },
                          }))
                        }
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  ))}
                </Grid>
              </Grid>

              <PageActionsEditor
                pageType="product_listing"
                config={config}
                setConfig={setConfig}
                getSelectorInputProps={getSelectorInputProps}
              />

              <JsonIslandsEditor pageType="product_listing" config={config} setConfig={setConfig} />

              {/* Fields for Product Listing */}
              <Box sx={{ mt: 3 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                  }}
                >
                  <Typography variant="subtitle1">Fields</Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      startIcon={<AddIcon />}
                      onClick={() => addField('product_listing')}
                      size="small"
                    >
                      Add Field
                    </Button>
                    <Button
                      startIcon={<AddIcon />}
                      onClick={addCommonProductListingFields}
                      size="small"
                      variant="outlined"
                    >
                      Add Common Fields
                    </Button>
                  </Box>
                </Box>

                {Object.entries(config.pages?.product_listing?.fields || {}).map(
                  ([fieldKey, field]) => {
                    const isInvalid = !isFieldValidForPage(fieldKey, 'product_listing');
                    return (
                      <Accordion
                        key={fieldKey}
                        sx={{
                          mb: 1,
                          border: isInvalid ? 2 : 0,
                          borderColor: isInvalid ? 'error.main' : 'transparent',
                          bgcolor: isInvalid ? 'error.light' : 'background.paper',
                        }}
                      >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Box
                            sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}
                          >
                            {isInvalid && (
                              <Tooltip
                                title={`Field '${fieldKey}' is not valid for product_listing page type`}
                              >
                                <Chip
                                  label="INVALID"
                                  color="error"
                                  size="small"
                                  sx={{ fontWeight: 'bold' }}
                                />
                              </Tooltip>
                            )}
                            <FormControl size="small" sx={{ minWidth: 220 }}>
                              <InputLabel>Field</InputLabel>
                              <Select
                                value={field.name || fieldKey}
                                label="Field"
                                onChange={e => {
                                  const choice = FIELD_REGISTRY.find(r => r.key === e.target.value);
                                  const newKey = String(e.target.value);
                                  const updates: any = {};
                                  if (choice?.data_type) updates.data_type = choice.data_type;
                                  if (choice?.default_attribute && !field.attribute)
                                    updates.attribute = choice.default_attribute;
                                  renameFieldKey('product_listing', fieldKey, newKey, updates);
                                }}
                              >
                                {getRegistryFor('product_listing')
                                  .filter(opt => {
                                    const currentKey = String(field.name || fieldKey);
                                    return (
                                      !usedFieldNames('product_listing').has(opt.key) ||
                                      opt.key === currentKey
                                    );
                                  })
                                  .map(opt => (
                                    <MenuItem key={opt.key} value={opt.key}>
                                      {opt.label}
                                    </MenuItem>
                                  ))}
                              </Select>
                            </FormControl>
                            <Chip
                              label={field.required ? 'Required' : 'Optional'}
                              color={field.required ? 'error' : 'default'}
                              size="small"
                            />
                            {enhancedFieldSet.has(`product_listing.fields.${fieldKey}`) && (
                              <Tooltip title="This field was modified by AI validation">
                                <Chip
                                  icon={<AutoFixHighIcon />}
                                  label="AI Enhanced"
                                  color="secondary"
                                  size="small"
                                  sx={{
                                    fontWeight: 'bold',
                                    animation: 'pulse 2s ease-in-out infinite',
                                    '@keyframes pulse': {
                                      '0%, 100%': { opacity: 1 },
                                      '50%': { opacity: 0.7 },
                                    },
                                  }}
                                />
                              </Tooltip>
                            )}
                            <IconButton
                              onClick={e => {
                                e.stopPropagation();
                                removeField('product_listing', fieldKey);
                              }}
                              size="small"
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={3}>
                              <FormControl fullWidth size="small">
                                <InputLabel>Data Type</InputLabel>
                                <Select
                                  value={field.data_type}
                                  label="Data Type"
                                  onChange={e =>
                                    updateField('product_listing', fieldKey, {
                                      data_type: e.target.value,
                                    })
                                  }
                                >
                                  <MenuItem value="string">String</MenuItem>
                                  <MenuItem value="string_list">String list</MenuItem>
                                  <MenuItem value="decimal">Decimal</MenuItem>
                                  <MenuItem value="url">URL</MenuItem>
                                  <MenuItem value="image">Image</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <FormControl fullWidth size="small">
                                <InputLabel>Selector Type</InputLabel>
                                <Select
                                  value={field.selector_type}
                                  label="Selector Type"
                                  onChange={e =>
                                    updateField('product_listing', fieldKey, {
                                      selector_type: e.target.value,
                                    })
                                  }
                                >
                                  <MenuItem value="css">CSS</MenuItem>
                                  <MenuItem value="json_path">JSONPath</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>
                            {!isJsonPathField(field) && (
                              <Grid item xs={12} md={3}>
                                <FormControl fullWidth size="small">
                                  <InputLabel>Source</InputLabel>
                                  <Select
                                    value={field.source || 'html'}
                                    label="Source"
                                    onChange={e =>
                                      updateField('product_listing', fieldKey, {
                                        source: e.target.value as any,
                                      })
                                    }
                                  >
                                    <MenuItem value="html">HTML Element</MenuItem>
                                    <MenuItem value="url">Request URL</MenuItem>
                                    <MenuItem value="document_location">Document Location</MenuItem>
                                  </Select>
                                </FormControl>
                              </Grid>
                            )}
                            {!isJsonPathField(field) &&
                              (field.source === 'html' || !field.source) &&
                              !(field as any).click_and_capture && (
                                <Grid item xs={12} md={3}>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    label="Attribute"
                                    value={field.attribute || ''}
                                    onChange={e =>
                                      updateField('product_listing', fieldKey, {
                                        attribute: e.target.value,
                                      })
                                    }
                                    placeholder="href, src, etc."
                                    helperText="HTML attribute to extract"
                                  />
                                </Grid>
                              )}
                            <Grid item xs={12} md={3}>
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={field.required}
                                    onChange={e =>
                                      updateField('product_listing', fieldKey, {
                                        required: e.target.checked,
                                      })
                                    }
                                  />
                                }
                                label="Required"
                              />
                            </Grid>
                            {!isJsonPathField(field) &&
                              (field.source === 'html' || !field.source) &&
                              !(field as any).click_and_capture &&
                              (field.selectors?.length ?? 0) > 0 && (
                                <Grid item xs={12} md={3}>
                                  <FormControlLabel
                                    control={
                                      <Switch
                                        checked={
                                          (config.pages?.product_listing as any)?.wait_for_field ===
                                          fieldKey
                                        }
                                        onChange={e => {
                                          setConfig(prev => ({
                                            ...prev,
                                            pages: {
                                              ...prev.pages,
                                              product_listing: {
                                                ...prev.pages?.product_listing,
                                                page_type: 'product_listing',
                                                wait_for_field: e.target.checked
                                                  ? fieldKey
                                                  : undefined,
                                              } as any,
                                            },
                                          }));
                                        }}
                                      />
                                    }
                                    label="Wait for before capture"
                                  />
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: 'block', mt: 0.5 }}
                                  >
                                    Wait for this selector before capturing HTML (for JS-rendered
                                    content)
                                  </Typography>
                                </Grid>
                              )}

                            {/* Helper text for source options */}
                            <Grid item xs={12}>
                              {isJsonPathField(field) ? (
                                <Typography variant="caption" color="text.secondary">
                                  JSONPath against a JSON island on this page. Use island:$.field,
                                  for example goods:$.minishopURL.
                                </Typography>
                              ) : field.source === 'html' || !field.source ? (
                                <Typography variant="caption" color="text.secondary">
                                  Extract from HTML element using CSS selector
                                </Typography>
                              ) : field.source === 'url' ? (
                                <Typography variant="caption" color="text.secondary">
                                  Extract from the original request URL (before any redirects)
                                </Typography>
                              ) : field.source === 'document_location' ? (
                                <Typography variant="caption" color="info.dark">
                                  Extract from the actual current URL (document.location) after any
                                  redirects or JavaScript navigation. Use with post-processing to
                                  clean URLs.
                                </Typography>
                              ) : null}
                            </Grid>

                            {/* Selectors - HTML/CSS, or JSONPath. Hidden for URL sources and click-and-capture. */}
                            {(isJsonPathField(field) || field.source === 'html' || !field.source) &&
                              !(field as any).click_and_capture && (
                                <Grid item xs={12}>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                      mb: 1,
                                    }}
                                  >
                                    <Typography variant="subtitle2">
                                      {isJsonPathField(field) ? 'JSONPath' : 'CSS Selectors'}
                                    </Typography>
                                    <Button
                                      startIcon={<AddIcon />}
                                      onClick={() => addSelector('product_listing', fieldKey)}
                                      size="small"
                                    >
                                      Add Selector
                                    </Button>
                                  </Box>

                                  {field.selectors.map((selector, selectorIndex) => (
                                    <Box
                                      key={selectorIndex}
                                      sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}
                                    >
                                      <TextField
                                        fullWidth
                                        size="small"
                                        label={`Selector ${selectorIndex + 1}`}
                                        value={selector}
                                        onChange={e =>
                                          updateSelector(
                                            'product_listing',
                                            fieldKey,
                                            selectorIndex,
                                            e.target.value
                                          )
                                        }
                                        placeholder={
                                          isJsonPathField(field)
                                            ? 'goods:$.minishopURL'
                                            : '#productTitle'
                                        }
                                        InputProps={
                                          isJsonPathField(field)
                                            ? undefined
                                            : getSelectorInputProps(selector)
                                        }
                                      />
                                      <IconButton
                                        onClick={() =>
                                          removeSelector('product_listing', fieldKey, selectorIndex)
                                        }
                                        size="small"
                                        color="error"
                                        disabled={field.selectors.length === 1}
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    </Box>
                                  ))}
                                </Grid>
                              )}

                            {/* Click and Capture Configuration (for images) */}
                            {!isJsonPathField(field) &&
                              fieldKey === 'images' &&
                              field.data_type === 'image' && (
                                <Grid item xs={12}>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                      mb: 1,
                                    }}
                                  >
                                    <Typography variant="subtitle2">Click and Capture</Typography>
                                    {!(field as any).click_and_capture && (
                                      <Button
                                        startIcon={<AddIcon />}
                                        onClick={() =>
                                          updateField('product_listing', fieldKey, {
                                            click_and_capture: {
                                              thumbnail_selector: '',
                                              main_viewer_selector: '',
                                              next_selector: '',
                                              prev_selector: '',
                                              wait_time: 500,
                                            },
                                          })
                                        }
                                        size="small"
                                      >
                                        Add Click and Capture
                                      </Button>
                                    )}
                                    {(field as any).click_and_capture && (
                                      <Button
                                        onClick={() =>
                                          updateField('product_listing', fieldKey, {
                                            click_and_capture: undefined,
                                          })
                                        }
                                        size="small"
                                        color="error"
                                      >
                                        Remove Click and Capture
                                      </Button>
                                    )}
                                  </Box>

                                  {(field as any).click_and_capture && (
                                    <Box
                                      sx={{
                                        p: 2,
                                        bgcolor: (theme: any) =>
                                          theme.palette.mode === 'dark'
                                            ? 'background.default'
                                            : 'grey.50',
                                        borderRadius: 1,
                                        border: (theme: any) =>
                                          `1px solid ${theme.palette.divider}`,
                                        mb: 2,
                                      }}
                                    >
                                      <Typography
                                        variant="caption"
                                        color="info.dark"
                                        sx={{ display: 'block', mb: 2 }}
                                      >
                                        Playwright will click each thumbnail and capture the
                                        high-resolution URL from the main viewer. Use this for
                                        platforms that restrict direct access to full-size images.
                                        For arrow-advanced carousels with no thumbnail rail (e.g.
                                        Instagram), leave Thumbnail Selector empty and set a Next
                                        Selector instead — it advances the viewer and captures every
                                        slide. Set a Previous Selector too so posts opened
                                        mid-carousel (e.g. Instagram ?img_index=N) rewind to the
                                        first slide before capturing.
                                      </Typography>
                                      <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                          <TextField
                                            fullWidth
                                            size="small"
                                            label="Thumbnail Selector"
                                            value={
                                              (field as any).click_and_capture.thumbnail_selector ||
                                              ''
                                            }
                                            onChange={e =>
                                              updateField('product_listing', fieldKey, {
                                                click_and_capture: {
                                                  ...(field as any).click_and_capture,
                                                  thumbnail_selector: e.target.value,
                                                },
                                              })
                                            }
                                            placeholder="button.image-thumbnail img"
                                            helperText="CSS selector for clickable thumbnail images"
                                            InputProps={getSelectorInputProps(
                                              (field as any).click_and_capture?.thumbnail_selector
                                            )}
                                          />
                                        </Grid>
                                        <Grid item xs={12}>
                                          <TextField
                                            fullWidth
                                            size="small"
                                            label="Main Viewer Selector"
                                            value={
                                              (field as any).click_and_capture
                                                .main_viewer_selector || ''
                                            }
                                            onChange={e =>
                                              updateField('product_listing', fieldKey, {
                                                click_and_capture: {
                                                  ...(field as any).click_and_capture,
                                                  main_viewer_selector: e.target.value,
                                                },
                                              })
                                            }
                                            placeholder="img.main-product-image"
                                            helperText="CSS selector for the main image viewer that updates when thumbnails are clicked"
                                            InputProps={getSelectorInputProps(
                                              (field as any).click_and_capture?.main_viewer_selector
                                            )}
                                          />
                                        </Grid>
                                        <Grid item xs={12}>
                                          <TextField
                                            fullWidth
                                            size="small"
                                            label="Next Selector (optional)"
                                            value={
                                              (field as any).click_and_capture.next_selector || ''
                                            }
                                            onChange={e =>
                                              updateField('product_listing', fieldKey, {
                                                click_and_capture: {
                                                  ...(field as any).click_and_capture,
                                                  next_selector: e.target.value,
                                                },
                                              })
                                            }
                                            placeholder='button[aria-label="Next"]'
                                            helperText="For arrow-advanced carousels with no thumbnails (e.g. Instagram): CSS selector for the next-slide control. Leave Thumbnail Selector empty when using this."
                                            InputProps={getSelectorInputProps(
                                              (field as any).click_and_capture?.next_selector
                                            )}
                                          />
                                        </Grid>
                                        <Grid item xs={12}>
                                          <TextField
                                            fullWidth
                                            size="small"
                                            label="Previous Selector (optional)"
                                            value={
                                              (field as any).click_and_capture.prev_selector || ''
                                            }
                                            onChange={e =>
                                              updateField('product_listing', fieldKey, {
                                                click_and_capture: {
                                                  ...(field as any).click_and_capture,
                                                  prev_selector: e.target.value,
                                                },
                                              })
                                            }
                                            placeholder='button[aria-label="Go back"]'
                                            helperText="CSS selector for the previous-slide control. Used to rewind to the first slide before capturing when a post opens mid-carousel (e.g. Instagram ?img_index=N)."
                                            InputProps={getSelectorInputProps(
                                              (field as any).click_and_capture?.prev_selector
                                            )}
                                          />
                                        </Grid>
                                        <Grid item xs={12}>
                                          <TextField
                                            fullWidth
                                            size="small"
                                            type="number"
                                            label="Wait Time (ms)"
                                            value={
                                              (field as any).click_and_capture.wait_time ?? 500
                                            }
                                            onChange={e =>
                                              updateField('product_listing', fieldKey, {
                                                click_and_capture: {
                                                  ...(field as any).click_and_capture,
                                                  wait_time: Number(e.target.value),
                                                },
                                              })
                                            }
                                            helperText="Wait time after clicking each thumbnail"
                                          />
                                        </Grid>
                                      </Grid>
                                    </Box>
                                  )}
                                </Grid>
                              )}

                            {/* Post-Processing Configuration */}
                            <Grid item xs={12}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  mb: 1,
                                }}
                              >
                                <Typography variant="subtitle2">Post-Processing</Typography>
                                {!field.post_processing && (
                                  <Button
                                    startIcon={<AddIcon />}
                                    onClick={() =>
                                      updatePostProcessing('product_listing', fieldKey, {
                                        type: 'strip',
                                      })
                                    }
                                    size="small"
                                  >
                                    Add Post-Processing
                                  </Button>
                                )}
                                {field.post_processing && (
                                  <Button
                                    onClick={() =>
                                      removePostProcessing('product_listing', fieldKey)
                                    }
                                    size="small"
                                    color="error"
                                  >
                                    Remove Post-Processing
                                  </Button>
                                )}
                              </Box>

                              {field.post_processing && (
                                <Box
                                  sx={{
                                    p: 2,
                                    bgcolor: (theme: any) =>
                                      theme.palette.mode === 'dark'
                                        ? 'background.default'
                                        : 'grey.50',
                                    borderRadius: 1,
                                    border: (theme: any) => `1px solid ${theme.palette.divider}`,
                                  }}
                                >
                                  <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                      <FormControl fullWidth size="small">
                                        <InputLabel>Processing Type</InputLabel>
                                        <Select
                                          value={field.post_processing.type}
                                          label="Processing Type"
                                          onChange={e =>
                                            updatePostProcessing('product_listing', fieldKey, {
                                              type: e.target.value as any,
                                            })
                                          }
                                        >
                                          <MenuItem value="trim_query_params">
                                            Trim Query Parameters
                                          </MenuItem>
                                          <MenuItem value="canonicalize_url">
                                            Canonicalize URL
                                          </MenuItem>
                                          <MenuItem value="custom">Custom Regex</MenuItem>
                                          <MenuItem value="python">Python</MenuItem>
                                          <MenuItem value="regex_replace">Regex Replace</MenuItem>
                                          <MenuItem value="strip">Strip Characters</MenuItem>
                                          <MenuItem value="remove_currency">
                                            Remove Currency
                                          </MenuItem>
                                          <MenuItem value="parse_quantity_abbrev">
                                            Parse Quantity Abbrev (RB/K/JT)
                                          </MenuItem>
                                          <MenuItem value="cleanse_currency">
                                            Clean Currency
                                          </MenuItem>
                                        </Select>
                                      </FormControl>
                                    </Grid>

                                    {field.post_processing.type === 'custom' && (
                                      <Grid item xs={12} md={6}>
                                        <TextField
                                          fullWidth
                                          size="small"
                                          label="Custom Pattern"
                                          value={field.post_processing.custom_pattern || ''}
                                          onChange={e =>
                                            updatePostProcessing('product_listing', fieldKey, {
                                              custom_pattern: e.target.value,
                                            })
                                          }
                                          placeholder="^.*?/(?:dp|gp/product)/([A-Z0-9]{10})(?:/|\\?|#|$)"
                                          helperText="Python re.match on the full value; first capture group is used if present, else the whole match"
                                          InputProps={getRegexInputProps(
                                            field.post_processing.custom_pattern,
                                            'Test custom post-processing regex',
                                            'match',
                                            '',
                                            true
                                          )}
                                        />
                                      </Grid>
                                    )}

                                    {field.post_processing.type === 'canonicalize_url' &&
                                      renderCanonicalizeUrlOptions(field.post_processing, updates =>
                                        updatePostProcessing('product_listing', fieldKey, updates)
                                      )}

                                    {field.post_processing.type === 'python' && (
                                      <Grid item xs={12}>
                                        <TextField
                                          fullWidth
                                          multiline
                                          minRows={2}
                                          maxRows={4}
                                          size="small"
                                          label={
                                            field.post_processing.type === 'python'
                                              ? 'Python Code'
                                              : 'JavaScript Code'
                                          }
                                          value={field.post_processing.code || ''}
                                          onChange={e =>
                                            updatePostProcessing('product_listing', fieldKey, {
                                              code: e.target.value,
                                            })
                                          }
                                          placeholder="url.match(/^(.*?\\/dp\\/[A-Z0-9]+).*$/)?.[1] || url"
                                          helperText="Code to process the field value. JavaScript: use 'value' variable. Python: use 'value' variable and set 'result' variable."
                                          sx={{
                                            '& .MuiInputBase-root': {
                                              fontFamily:
                                                'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                                              bgcolor: (theme: any) =>
                                                theme.palette.mode === 'dark'
                                                  ? 'background.paper'
                                                  : 'background.default',
                                            },
                                          }}
                                        />
                                      </Grid>
                                    )}

                                    {field.post_processing.type === 'regex_replace' && (
                                      <>
                                        <Grid item xs={12} md={6}>
                                          <TextField
                                            fullWidth
                                            size="small"
                                            label="Pattern"
                                            value={field.post_processing.pattern || ''}
                                            onChange={e =>
                                              updatePostProcessing('product_listing', fieldKey, {
                                                pattern: e.target.value,
                                              })
                                            }
                                            placeholder="\\$|USD|€|EUR"
                                            helperText="Regex pattern to find"
                                            InputProps={getRegexInputProps(
                                              field.post_processing.pattern,
                                              'Test regex replacement',
                                              'replace',
                                              field.post_processing.replacement || ''
                                            )}
                                          />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                          <TextField
                                            fullWidth
                                            size="small"
                                            label="Replacement"
                                            value={field.post_processing.replacement || ''}
                                            onChange={e =>
                                              updatePostProcessing('product_listing', fieldKey, {
                                                replacement: e.target.value,
                                              })
                                            }
                                            placeholder=""
                                            helperText="Replacement text"
                                          />
                                        </Grid>
                                      </>
                                    )}

                                    {field.post_processing.type === 'strip' && (
                                      <Grid item xs={12} md={6}>
                                        <TextField
                                          fullWidth
                                          size="small"
                                          label="Characters to Strip"
                                          value={field.post_processing.chars || ' \t\n\r'}
                                          onChange={e =>
                                            updatePostProcessing('product_listing', fieldKey, {
                                              chars: e.target.value,
                                            })
                                          }
                                          placeholder=" \t\n\r"
                                          helperText="Characters to remove from start/end"
                                        />
                                      </Grid>
                                    )}
                                  </Grid>
                                </Box>
                              )}
                            </Grid>
                          </Grid>
                        </AccordionDetails>
                      </Accordion>
                    );
                  }
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Seller Navigation Configuration (moved above Seller Page) */}
        <Grid item xs={12}>
          <Card
            sx={
              definition.parent_definition_id && parentConfig
                ? {
                    border: isSellerNavigationInherited()
                      ? '2px solid #e0e0e0'
                      : '2px solid #ed6c02',
                    backgroundColor: isSellerNavigationInherited()
                      ? 'rgba(0, 0, 0, 0.02)'
                      : 'inherit',
                  }
                : {}
            }
          >
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6">Seller Navigation</Typography>
                  {definition.parent_definition_id && parentConfig && (
                    <Chip
                      label={isSellerNavigationInherited() ? 'Inherited' : 'Overridden'}
                      size="small"
                      color={isSellerNavigationInherited() ? 'default' : 'warning'}
                      variant={isSellerNavigationInherited() ? 'outlined' : 'filled'}
                    />
                  )}
                </Box>
                {definition.parent_definition_id &&
                  parentConfig &&
                  !isSellerNavigationInherited() && (
                    <Tooltip title="Reset to inherited configuration from parent template">
                      <Button
                        size="small"
                        startIcon={<RestoreIcon />}
                        onClick={() => resetSellerNavigationToInherited()}
                        color="warning"
                      >
                        Reset to Inherited
                      </Button>
                    </Tooltip>
                  )}
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Configure how to navigate from product pages to seller pages. Define the steps
                needed to locate and visit seller profiles after scraping product listings.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Mode</InputLabel>
                    <Select
                      label="Mode"
                      value={(config as any)?.seller_navigation?.mode || 'multiple'}
                      onChange={e =>
                        setConfig(prev => ({
                          ...prev,
                          seller_navigation: {
                            ...(prev as any).seller_navigation,
                            mode: e.target.value,
                          },
                        }))
                      }
                    >
                      <MenuItem value="single">single</MenuItem>
                      <MenuItem value="multiple">multiple</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                {sellerNavigationMode !== 'single' && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Max Sellers"
                      value={(config as any)?.seller_navigation?.max_sellers ?? 10}
                      onChange={e =>
                        setConfig(prev => ({
                          ...prev,
                          seller_navigation: {
                            ...(prev as any).seller_navigation,
                            max_sellers: Number(e.target.value),
                          },
                        }))
                      }
                      inputProps={{ min: 1, max: 50 }}
                    />
                  </Grid>
                )}
              </Grid>

              <Box sx={{ mt: 3 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 1,
                  }}
                >
                  <Typography variant="subtitle1">From Product Steps</Typography>
                  <Button
                    startIcon={<AddIcon />}
                    size="small"
                    onClick={() =>
                      setConfig(prev => ({
                        ...prev,
                        seller_navigation: {
                          ...(prev as any).seller_navigation,
                          from_product: [
                            ...((prev as any).seller_navigation?.from_product || []),
                            { type: 'extract', selector: '', attribute: 'href' },
                          ],
                        },
                      }))
                    }
                  >
                    Add Step
                  </Button>
                </Box>
                {((config as any)?.seller_navigation?.from_product || []).map(
                  (step: any, idx: number) => (
                    <Accordion key={idx} sx={{ mb: 1 }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        Step {idx + 1}
                      </AccordionSummary>
                      <AccordionDetails>
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={12} md={2}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Type</InputLabel>
                              <Select
                                label="Type"
                                value={step.type || 'extract'}
                                onChange={e => {
                                  const next = [
                                    ...((config as any).seller_navigation?.from_product || []),
                                  ];
                                  next[idx] = { ...step, type: e.target.value };
                                  setConfig((prev: any) => ({
                                    ...prev,
                                    seller_navigation: {
                                      ...prev.seller_navigation,
                                      from_product: next,
                                    },
                                  }));
                                }}
                              >
                                <MenuItem value="extract">extract</MenuItem>
                                <MenuItem value="iterate">iterate</MenuItem>
                                <MenuItem value="click">click</MenuItem>
                                <MenuItem value="follow">follow</MenuItem>
                                <MenuItem value="field">field</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12}>
                            <Box
                              sx={{
                                p: 1,
                                bgcolor: 'info.lighter',
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'info.light',
                              }}
                            >
                              <Typography variant="caption" color="info.dark">
                                <strong>{step.type || 'extract'}:</strong>{' '}
                                {STEP_TYPE_DESCRIPTIONS[step.type || 'extract']}
                              </Typography>
                            </Box>
                          </Grid>
                          {(step.type || 'extract') === 'extract' && (
                            <Grid item xs={12} md={3}>
                              <FormControl fullWidth size="small">
                                <InputLabel>Selector Type</InputLabel>
                                <Select
                                  label="Selector Type"
                                  value={step.selector_type || 'css'}
                                  onChange={e => {
                                    const next = [
                                      ...((config as any).seller_navigation?.from_product || []),
                                    ];
                                    next[idx] = { ...step, selector_type: e.target.value };
                                    setConfig((prev: any) => ({
                                      ...prev,
                                      seller_navigation: {
                                        ...prev.seller_navigation,
                                        from_product: next,
                                      },
                                    }));
                                  }}
                                >
                                  <MenuItem value="css">CSS</MenuItem>
                                  <MenuItem value="json_path">JSONPath</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>
                          )}
                          {isJsonPathNavStep(step) && (
                            <Grid item xs={12}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Script id"
                                value={step.script_id || ''}
                                onChange={e => {
                                  const next = [
                                    ...((config as any).seller_navigation?.from_product || []),
                                  ];
                                  next[idx] = { ...step, script_id: e.target.value };
                                  setConfig((prev: any) => ({
                                    ...prev,
                                    seller_navigation: {
                                      ...prev.seller_navigation,
                                      from_product: next,
                                    },
                                  }));
                                }}
                                placeholder="goods-consts-data"
                                helperText="id of the script element whose text is JSON"
                              />
                            </Grid>
                          )}
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              size="small"
                              label={
                                step.type === 'field'
                                  ? 'Field Name'
                                  : isJsonPathNavStep(step)
                                    ? 'JSONPath'
                                    : 'Selector'
                              }
                              value={step.selector || ''}
                              onChange={e => {
                                const next = [
                                  ...((config as any).seller_navigation?.from_product || []),
                                ];
                                next[idx] = { ...step, selector: e.target.value };
                                setConfig((prev: any) => ({
                                  ...prev,
                                  seller_navigation: {
                                    ...prev.seller_navigation,
                                    from_product: next,
                                  },
                                }));
                              }}
                              placeholder={
                                isJsonPathNavStep(step)
                                  ? '$.minishopURL'
                                  : step.type === 'click'
                                    ? 'div#rightCol a#aod-ingress-link'
                                    : step.type === 'field'
                                      ? 'seller_store_url'
                                      : "a[href*='/aag/main'], a[href*='?me=']"
                              }
                              helperText={
                                isJsonPathNavStep(step)
                                  ? 'Field inside that script. This becomes the seller store URL.'
                                  : undefined
                              }
                              InputProps={
                                step.type !== 'field' && !isJsonPathNavStep(step)
                                  ? getSelectorInputProps(step.selector)
                                  : undefined
                              }
                            />
                          </Grid>
                          {!isJsonPathNavStep(step) && (
                            <Grid item xs={12}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Attribute"
                                value={step.type === 'click' ? '' : step.attribute || 'href'}
                                onChange={e => {
                                  const next = [
                                    ...((config as any).seller_navigation?.from_product || []),
                                  ];
                                  next[idx] = { ...step, attribute: e.target.value };
                                  setConfig((prev: any) => ({
                                    ...prev,
                                    seller_navigation: {
                                      ...prev.seller_navigation,
                                      from_product: next,
                                    },
                                  }));
                                }}
                                disabled={step.type === 'click'}
                                helperText={
                                  step.type === 'click'
                                    ? 'Not used for click steps'
                                    : 'href, src, etc.'
                                }
                              />
                            </Grid>
                          )}
                          {!isJsonPathNavStep(step) && (
                            <Grid item xs={12}>
                              <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label="Limit"
                                value={step.limit ?? ''}
                                onChange={e => {
                                  const next = [
                                    ...((config as any).seller_navigation?.from_product || []),
                                  ];
                                  const v =
                                    e.target.value === '' ? undefined : Number(e.target.value);
                                  next[idx] = { ...step, limit: v };
                                  setConfig((prev: any) => ({
                                    ...prev,
                                    seller_navigation: {
                                      ...prev.seller_navigation,
                                      from_product: next,
                                    },
                                  }));
                                }}
                                helperText={
                                  step.type === 'iterate'
                                    ? 'Used by iterate'
                                    : step.type === 'click'
                                      ? 'Wait time after click (ms)'
                                      : ''
                                }
                              />
                            </Grid>
                          )}
                          {/* Debug: step.type={step.type}, step.attribute={step.attribute} */}
                          {step.type === 'extract' && step.attribute === 'href' && (
                            <Grid item xs={12}>
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={step.normalize?.prepend_host_if_relative || false}
                                    onChange={e => {
                                      const next = [
                                        ...((config as any).seller_navigation?.from_product || []),
                                      ];
                                      next[idx] = {
                                        ...step,
                                        normalize: {
                                          ...step.normalize,
                                          prepend_host_if_relative: e.target.checked,
                                        },
                                      };
                                      setConfig((prev: any) => ({
                                        ...prev,
                                        seller_navigation: {
                                          ...prev.seller_navigation,
                                          from_product: next,
                                        },
                                      }));
                                    }}
                                  />
                                }
                                label="Prepend host if relative URL"
                              />
                            </Grid>
                          )}
                          {step.type === 'extract' && (
                            <Grid item xs={12}>
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={
                                      step.preserve_query_params === true ||
                                      step.trim_query_params === false
                                    }
                                    onChange={e => {
                                      const next = [
                                        ...((config as any).seller_navigation?.from_product || []),
                                      ];
                                      if (e.target.checked) {
                                        // Preserve query params: set preserve_query_params to true
                                        // and remove trim_query_params if it exists
                                        const updated = { ...step, preserve_query_params: true };
                                        delete (updated as any).trim_query_params;
                                        next[idx] = updated;
                                      } else {
                                        // Don't preserve: remove preserve_query_params
                                        const updated = { ...step };
                                        delete (updated as any).preserve_query_params;
                                        next[idx] = updated;
                                      }
                                      setConfig((prev: any) => ({
                                        ...prev,
                                        seller_navigation: {
                                          ...prev.seller_navigation,
                                          from_product: next,
                                        },
                                      }));
                                    }}
                                  />
                                }
                                label="Preserve query parameters"
                              />
                            </Grid>
                          )}

                          {/* Post-processing for extract steps */}
                          {step.type === 'extract' && (
                            <Grid item xs={12}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  mb: 1,
                                }}
                              >
                                <Typography variant="subtitle2">Post-processing</Typography>
                                {step.post_processing ? (
                                  <Button
                                    onClick={() => {
                                      const next = [
                                        ...((config as any).seller_navigation?.from_product || []),
                                      ];
                                      next[idx] = { ...step, post_processing: undefined };
                                      setConfig((prev: any) => ({
                                        ...prev,
                                        seller_navigation: {
                                          ...prev.seller_navigation,
                                          from_product: next,
                                        },
                                      }));
                                    }}
                                    size="small"
                                    color="error"
                                  >
                                    Remove Post-processing
                                  </Button>
                                ) : (
                                  <Button
                                    onClick={() => {
                                      const next = [
                                        ...((config as any).seller_navigation?.from_product || []),
                                      ];
                                      next[idx] = {
                                        ...step,
                                        post_processing: {
                                          type: 'python',
                                          code: '',
                                        },
                                      };
                                      setConfig((prev: any) => ({
                                        ...prev,
                                        seller_navigation: {
                                          ...prev.seller_navigation,
                                          from_product: next,
                                        },
                                      }));
                                    }}
                                    size="small"
                                    startIcon={<AddIcon />}
                                  >
                                    Add Post-processing
                                  </Button>
                                )}
                              </Box>
                              {step.post_processing && (
                                <Box sx={{ mb: 2 }}>
                                  <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                                    <InputLabel>Type</InputLabel>
                                    <Select
                                      value={step.post_processing.type || 'python'}
                                      label="Type"
                                      onChange={e => {
                                        const next = [
                                          ...((config as any).seller_navigation?.from_product ||
                                            []),
                                        ];
                                        next[idx] = {
                                          ...step,
                                          post_processing: {
                                            ...step.post_processing,
                                            type: e.target.value as any,
                                          },
                                        };
                                        setConfig((prev: any) => ({
                                          ...prev,
                                          seller_navigation: {
                                            ...prev.seller_navigation,
                                            from_product: next,
                                          },
                                        }));
                                      }}
                                    >
                                      <MenuItem value="trim_query_params">
                                        Trim Query Parameters
                                      </MenuItem>
                                      <MenuItem value="canonicalize_url">Canonicalize URL</MenuItem>
                                      <MenuItem value="python">Python</MenuItem>
                                      <MenuItem value="regex_replace">Regex Replace</MenuItem>
                                    </Select>
                                  </FormControl>
                                  {step.post_processing.type === 'trim_query_params' && (
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{ mb: 1 }}
                                    >
                                      Removes all query parameters from the extracted URL
                                      (everything after the first '?').
                                    </Typography>
                                  )}
                                  {step.post_processing.type === 'canonicalize_url' && (
                                    <Grid container spacing={2} sx={{ mb: 1 }}>
                                      {renderCanonicalizeUrlOptions(
                                        step.post_processing,
                                        updates => {
                                          const next = [
                                            ...((config as any).seller_navigation?.from_product ||
                                              []),
                                          ];
                                          next[idx] = {
                                            ...step,
                                            post_processing: {
                                              ...step.post_processing,
                                              ...updates,
                                            },
                                          };
                                          setConfig((prev: any) => ({
                                            ...prev,
                                            seller_navigation: {
                                              ...prev.seller_navigation,
                                              from_product: next,
                                            },
                                          }));
                                        }
                                      )}
                                    </Grid>
                                  )}
                                  {step.post_processing.type === 'python' && (
                                    <TextField
                                      fullWidth
                                      multiline
                                      rows={4}
                                      size="small"
                                      label="Python Code"
                                      value={step.post_processing.code || ''}
                                      onChange={e => {
                                        const next = [
                                          ...((config as any).seller_navigation?.from_product ||
                                            []),
                                        ];
                                        next[idx] = {
                                          ...step,
                                          post_processing: {
                                            ...step.post_processing,
                                            code: e.target.value,
                                          },
                                        };
                                        setConfig((prev: any) => ({
                                          ...prev,
                                          seller_navigation: {
                                            ...prev.seller_navigation,
                                            from_product: next,
                                          },
                                        }));
                                      }}
                                      placeholder="# Transform the extracted URL to company profile URL&#10;from urllib.parse import urlparse, urlunparse&#10;&#10;if value:&#10;    parsed = urlparse(value)&#10;    # Replace index.html with company_profile.html&#10;    new_parsed = parsed._replace(path='/company_profile.html')&#10;    # Remove all query parameters&#10;    new_parsed = new_parsed._replace(query='', fragment='')&#10;    value = urlunparse(new_parsed)&#10;&#10;return value"
                                      helperText="Use 'value' variable to reference the extracted value"
                                    />
                                  )}
                                  {step.post_processing.type === 'regex_replace' && (
                                    <>
                                      <TextField
                                        fullWidth
                                        size="small"
                                        label="Pattern"
                                        value={step.post_processing.pattern || ''}
                                        onChange={e => {
                                          const next = [
                                            ...((config as any).seller_navigation?.from_product ||
                                              []),
                                          ];
                                          next[idx] = {
                                            ...step,
                                            post_processing: {
                                              ...step.post_processing,
                                              pattern: e.target.value,
                                            },
                                          };
                                          setConfig((prev: any) => ({
                                            ...prev,
                                            seller_navigation: {
                                              ...prev.seller_navigation,
                                              from_product: next,
                                            },
                                          }));
                                        }}
                                        placeholder="index\\.html.*"
                                        helperText="Regular expression pattern"
                                        sx={{ mb: 1 }}
                                        InputProps={getRegexInputProps(
                                          step.post_processing.pattern,
                                          'Test seller-navigation regex replacement',
                                          'replace',
                                          step.post_processing.replacement || ''
                                        )}
                                      />
                                      <TextField
                                        fullWidth
                                        size="small"
                                        label="Replacement"
                                        value={step.post_processing.replacement || ''}
                                        onChange={e => {
                                          const next = [
                                            ...((config as any).seller_navigation?.from_product ||
                                              []),
                                          ];
                                          next[idx] = {
                                            ...step,
                                            post_processing: {
                                              ...step.post_processing,
                                              replacement: e.target.value,
                                            },
                                          };
                                          setConfig((prev: any) => ({
                                            ...prev,
                                            seller_navigation: {
                                              ...prev.seller_navigation,
                                              from_product: next,
                                            },
                                          }));
                                        }}
                                        placeholder="company_profile.html"
                                        helperText="Replacement string"
                                      />
                                    </>
                                  )}
                                </Box>
                              )}
                            </Grid>
                          )}

                          {/* Additional fields for follow steps */}
                          {step.type === 'follow' && (
                            <>
                              <Grid item xs={12} md={6}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="Wait For Selector"
                                  value={step.wait_for || ''}
                                  onChange={e => {
                                    const next = [
                                      ...((config as any).seller_navigation?.from_product || []),
                                    ];
                                    next[idx] = { ...step, wait_for: e.target.value };
                                    setConfig((prev: any) => ({
                                      ...prev,
                                      seller_navigation: {
                                        ...prev.seller_navigation,
                                        from_product: next,
                                      },
                                    }));
                                  }}
                                  placeholder=".company-rating, .seller-rating"
                                  helperText="CSS selector to wait for after navigation"
                                  InputProps={getSelectorInputProps(step.wait_for)}
                                />
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={step.open_in_new_page || false}
                                      onChange={e => {
                                        const next = [
                                          ...((config as any).seller_navigation?.from_product ||
                                            []),
                                        ];
                                        next[idx] = { ...step, open_in_new_page: e.target.checked };
                                        setConfig((prev: any) => ({
                                          ...prev,
                                          seller_navigation: {
                                            ...prev.seller_navigation,
                                            from_product: next,
                                          },
                                        }));
                                      }}
                                    />
                                  }
                                  label="Open in New Page"
                                />
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={step.capture_url || false}
                                      onChange={e => {
                                        const next = [
                                          ...((config as any).seller_navigation?.from_product ||
                                            []),
                                        ];
                                        next[idx] = { ...step, capture_url: e.target.checked };
                                        setConfig((prev: any) => ({
                                          ...prev,
                                          seller_navigation: {
                                            ...prev.seller_navigation,
                                            from_product: next,
                                          },
                                        }));
                                      }}
                                    />
                                  }
                                  label="Capture URL"
                                />
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  display="block"
                                  sx={{ mt: 0.5 }}
                                >
                                  Click with Playwright and capture the final URL after navigation
                                  (includes query params)
                                </Typography>
                              </Grid>
                            </>
                          )}
                          <Grid item xs={12}>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button
                                size="small"
                                color="error"
                                onClick={() => {
                                  const next = [
                                    ...((config as any).seller_navigation?.from_product || []),
                                  ];
                                  next.splice(idx, 1);
                                  setConfig((prev: any) => ({
                                    ...prev,
                                    seller_navigation: {
                                      ...prev.seller_navigation,
                                      from_product: next,
                                    },
                                  }));
                                }}
                              >
                                Remove Step
                              </Button>
                            </Box>
                          </Grid>
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  )
                )}
              </Box>

              {/* Test URLs for AI Validation moved to the global section above. */}
            </CardContent>
          </Card>
        </Grid>

        {/* Seller Page Configuration */}
        <Grid item xs={12}>
          <Card
            sx={
              definition.parent_definition_id && parentConfig
                ? {
                    border: isPageConfigInherited('seller_page')
                      ? '2px solid #e0e0e0'
                      : '2px solid #ed6c02',
                    backgroundColor: isPageConfigInherited('seller_page')
                      ? 'rgba(0, 0, 0, 0.02)'
                      : 'inherit',
                  }
                : {}
            }
          >
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6">Seller Page Configuration</Typography>
                  {definition.parent_definition_id && parentConfig && (
                    <Chip
                      label={isPageConfigInherited('seller_page') ? 'Inherited' : 'Overridden'}
                      size="small"
                      color={isPageConfigInherited('seller_page') ? 'default' : 'warning'}
                      variant={isPageConfigInherited('seller_page') ? 'outlined' : 'filled'}
                    />
                  )}
                </Box>
                {definition.parent_definition_id &&
                  parentConfig &&
                  !isPageConfigInherited('seller_page') && (
                    <Tooltip title="Reset to inherited configuration from parent template">
                      <Button
                        size="small"
                        startIcon={<RestoreIcon />}
                        onClick={() => resetPageToInherited('seller_page')}
                        color="warning"
                      >
                        Reset to Inherited
                      </Button>
                    </Tooltip>
                  )}
              </Box>

              {/* Info about field processing order */}
              {Object.values((config.pages as any)?.seller_page?.fields || {}).some(
                (f: any) => f.click
              ) && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <strong>Field Processing Order:</strong> Fields without click navigation will be
                  extracted first from the initial page. Fields with click navigation will be
                  processed last to avoid extracting from the wrong page after navigation.
                </Alert>
              )}

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="URL Pattern (regex)"
                    value={(config.pages as any)?.seller_page?.url_pattern || ''}
                    onChange={e =>
                      setConfig(prev => ({
                        ...prev,
                        pages: {
                          ...prev.pages,
                          seller_page: {
                            ...(prev.pages as any)?.seller_page,
                            page_type: 'seller_page' as any,
                            url_pattern: e.target.value,
                          },
                        },
                      }))
                    }
                    placeholder=".*seller.*|.*shop.*"
                    helperText="Regex to detect seller profile URLs"
                    InputProps={getRegexInputProps(
                      (config.pages as any)?.seller_page?.url_pattern,
                      'Test Seller Profile URL Pattern'
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Content Container Selector"
                    value={(config.pages as any)?.seller_page?.content_container || ''}
                    onChange={e =>
                      setConfig(prev => ({
                        ...prev,
                        pages: {
                          ...prev.pages,
                          seller_page: {
                            ...(prev.pages as any)?.seller_page,
                            page_type: 'seller_page' as any,
                            content_container: e.target.value,
                          },
                        },
                      }))
                    }
                    placeholder="#sellerProfile"
                    helperText="Main container for seller details"
                    InputProps={getSelectorInputProps(
                      (config.pages as any)?.seller_page?.content_container
                    )}
                  />
                </Grid>
              </Grid>

              {/* Fields for Seller Page */}
              <Box sx={{ mt: 3 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                  }}
                >
                  <Typography variant="subtitle1">Fields</Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      startIcon={<AddIcon />}
                      onClick={() => addField('seller_page' as any)}
                      size="small"
                    >
                      Add Field
                    </Button>
                  </Box>
                </Box>

                {Object.entries(
                  ((config.pages as any)?.seller_page?.fields || {}) as Record<
                    string,
                    FieldDefinition
                  >
                ).map(([fieldKey, field]) => (
                  <Accordion
                    key={fieldKey}
                    sx={{ mb: 1 }}
                    expanded={expandedSellerFields.has(fieldKey)}
                    onChange={(event, isExpanded) => {
                      // Allow manual toggle unless the event is coming from a form control
                      const target = event.target as HTMLElement;
                      const isFormControl = target.closest('input, select, button, textarea');

                      if (!isFormControl) {
                        setExpandedSellerFields(prev => {
                          const newSet = new Set(prev);
                          if (isExpanded) {
                            newSet.add(fieldKey);
                          } else {
                            newSet.delete(fieldKey);
                          }
                          return newSet;
                        });
                      }
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <FormControl size="small" sx={{ minWidth: 220 }}>
                          <InputLabel>Field</InputLabel>
                          <Select
                            value={field.name || fieldKey}
                            label="Field"
                            onChange={e => {
                              const choice = FIELD_REGISTRY.find(r => r.key === e.target.value);
                              const newKey = String(e.target.value);
                              const updates: any = {};
                              if (choice?.data_type) updates.data_type = choice.data_type;
                              if (choice?.default_attribute && !field.attribute)
                                updates.attribute = choice.default_attribute;
                              renameFieldKey('seller_page' as any, fieldKey, newKey, updates);

                              // Keep the accordion open when a field is selected
                              // Use the new key since the field was renamed
                              setTimeout(() => {
                                setExpandedSellerFields(prev => {
                                  const newSet = new Set(prev);
                                  newSet.delete(fieldKey); // Remove old key
                                  newSet.add(newKey); // Add new key
                                  return newSet;
                                });
                              }, 0);
                            }}
                          >
                            {getRegistryFor('seller_page')
                              .filter(opt => {
                                const currentKey = String(field.name || fieldKey);
                                return (
                                  !usedFieldNames('seller_page' as any).has(opt.key) ||
                                  opt.key === currentKey
                                );
                              })
                              .map(opt => (
                                <MenuItem key={opt.key} value={opt.key}>
                                  {opt.label}
                                </MenuItem>
                              ))}
                          </Select>
                        </FormControl>
                        <Chip
                          label={field.required ? 'Required' : 'Optional'}
                          color={field.required ? 'error' : 'default'}
                          size="small"
                        />
                        {enhancedFieldSet.has(`seller_page.fields.${fieldKey}`) && (
                          <Tooltip title="This field was modified by AI validation">
                            <Chip
                              icon={<AutoFixHighIcon />}
                              label="AI Enhanced"
                              color="secondary"
                              size="small"
                              sx={{
                                fontWeight: 'bold',
                                animation: 'pulse 2s ease-in-out infinite',
                                '@keyframes pulse': {
                                  '0%, 100%': { opacity: 1 },
                                  '50%': { opacity: 0.7 },
                                },
                              }}
                            />
                          </Tooltip>
                        )}
                        <IconButton
                          onClick={e => {
                            e.stopPropagation();
                            removeField('seller_page' as any, fieldKey);
                          }}
                          size="small"
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={3}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Data Type</InputLabel>
                            <Select
                              value={field.data_type}
                              label="Data Type"
                              onChange={e =>
                                updateField('seller_page' as any, fieldKey, {
                                  data_type: e.target.value as any,
                                })
                              }
                            >
                              <MenuItem value="string">String</MenuItem>
                              <MenuItem value="decimal">Decimal</MenuItem>
                              <MenuItem value="url">URL</MenuItem>
                              <MenuItem value="image">Image</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Selector Type</InputLabel>
                            <Select
                              value={field.selector_type}
                              label="Selector Type"
                              onChange={e =>
                                updateField('seller_page' as any, fieldKey, {
                                  selector_type: e.target.value as any,
                                })
                              }
                            >
                              <MenuItem value="css">CSS</MenuItem>
                              <MenuItem value="json_path">JSONPath</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        {!isJsonPathField(field) && (
                          <Grid item xs={12} md={3}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Source</InputLabel>
                              <Select
                                value={field.source || 'html'}
                                label="Source"
                                onChange={e =>
                                  updateField('seller_page' as any, fieldKey, {
                                    source: e.target.value as any,
                                  })
                                }
                              >
                                <MenuItem value="html">HTML Element</MenuItem>
                                <MenuItem value="url">Request URL</MenuItem>
                                <MenuItem value="document_location">Document Location</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                        )}
                        <Grid item xs={12}>
                          {isJsonPathField(field) ? (
                            <Typography variant="caption" color="text.secondary">
                              JSONPath against a JSON island on this page. Use island:$.field, for
                              example goods:$.minishopURL.
                            </Typography>
                          ) : field.source === 'html' ? (
                            <Typography variant="caption" color="text.secondary">
                              Extract from HTML element using CSS selector
                            </Typography>
                          ) : null}
                          {!isJsonPathField(field) && field.source === 'url' && (
                            <Typography variant="caption" color="text.secondary">
                              Extract from the original request URL (before any redirects)
                            </Typography>
                          )}
                          {!isJsonPathField(field) && field.source === 'document_location' && (
                            <Typography variant="caption" color="info.dark">
                              Extract from the actual current URL (document.location) after any
                              redirects or JavaScript navigation. Use with post-processing to clean
                              URLs.
                            </Typography>
                          )}
                        </Grid>
                        {!isJsonPathField(field) && (field.source === 'html' || !field.source) && (
                          <Grid item xs={12} md={3}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Attribute"
                              value={field.attribute || ''}
                              onChange={e =>
                                updateField('seller_page' as any, fieldKey, {
                                  attribute: e.target.value,
                                })
                              }
                              placeholder="href, src, etc."
                              helperText="HTML attribute to extract"
                            />
                          </Grid>
                        )}
                        <Grid item xs={12} md={3}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={field.required}
                                onChange={e =>
                                  updateField('seller_page' as any, fieldKey, {
                                    required: e.target.checked,
                                  })
                                }
                              />
                            }
                            label="Required"
                          />
                        </Grid>

                        {/* Selectors - HTML/CSS, or JSONPath */}
                        {(isJsonPathField(field) || field.source === 'html' || !field.source) && (
                          <Grid item xs={12}>
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                mb: 1,
                              }}
                            >
                              <Typography variant="subtitle2">
                                {isJsonPathField(field) ? 'JSONPath' : 'CSS Selectors'}
                              </Typography>
                              <Button
                                startIcon={<AddIcon />}
                                onClick={() => addSelector('seller_page' as any, fieldKey)}
                                size="small"
                              >
                                Add Selector
                              </Button>
                            </Box>
                            {(field.selectors || []).map((selector, selectorIndex) => (
                              <Box
                                key={selectorIndex}
                                sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}
                              >
                                <TextField
                                  fullWidth
                                  size="small"
                                  label={`Selector ${selectorIndex + 1}`}
                                  value={selector}
                                  onChange={e =>
                                    updateSelector(
                                      'seller_page' as any,
                                      fieldKey,
                                      selectorIndex,
                                      e.target.value
                                    )
                                  }
                                  placeholder=".seller-name"
                                  InputProps={getSelectorInputProps(selector)}
                                />
                                <IconButton
                                  onClick={() =>
                                    removeSelector('seller_page' as any, fieldKey, selectorIndex)
                                  }
                                  size="small"
                                  color="error"
                                  disabled={field.selectors.length === 1}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Box>
                            ))}
                          </Grid>
                        )}

                        {/* Post-processing */}
                        <Grid item xs={12}>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              mb: 1,
                            }}
                          >
                            <Typography variant="subtitle2">Post-processing</Typography>
                            {field.post_processing ? (
                              <Button
                                onClick={() => removePostProcessing('seller_page' as any, fieldKey)}
                                size="small"
                                color="error"
                              >
                                Remove Post-processing
                              </Button>
                            ) : (
                              <Button
                                onClick={() =>
                                  updatePostProcessing('seller_page' as any, fieldKey, {
                                    type: 'python',
                                    code: '',
                                  })
                                }
                                size="small"
                                startIcon={<AddIcon />}
                              >
                                Add Post-processing
                              </Button>
                            )}
                          </Box>
                          {field.post_processing && (
                            <Box sx={{ mb: 2 }}>
                              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                                <InputLabel>Type</InputLabel>
                                <Select
                                  value={field.post_processing.type || 'python'}
                                  label="Type"
                                  onChange={e =>
                                    updatePostProcessing('seller_page' as any, fieldKey, {
                                      type: e.target.value as any,
                                    })
                                  }
                                >
                                  <MenuItem value="python">Python</MenuItem>
                                  <MenuItem value="canonicalize_url">Canonicalize URL</MenuItem>
                                  <MenuItem value="regex_replace">Regex Replace</MenuItem>
                                  <MenuItem value="parse_quantity_abbrev">
                                    Parse Quantity Abbrev (RB/K/JT)
                                  </MenuItem>
                                  <MenuItem value="cleanse_currency">Clean Currency</MenuItem>
                                  <MenuItem value="remove_currency">Remove Currency</MenuItem>
                                </Select>
                              </FormControl>
                              {field.post_processing.type === 'canonicalize_url' && (
                                <Grid container spacing={2} sx={{ mb: 1 }}>
                                  {renderCanonicalizeUrlOptions(field.post_processing, updates =>
                                    updatePostProcessing('seller_page' as any, fieldKey, updates)
                                  )}
                                </Grid>
                              )}
                              {field.post_processing.type === 'python' && (
                                <TextField
                                  fullWidth
                                  multiline
                                  rows={4}
                                  size="small"
                                  label="Python Code"
                                  value={field.post_processing.code || ''}
                                  onChange={e =>
                                    updatePostProcessing('seller_page' as any, fieldKey, {
                                      code: e.target.value,
                                    })
                                  }
                                  placeholder="// JavaScript code to process the extracted value&#10;return value.trim();"
                                  helperText="Use 'value' variable to reference the extracted value"
                                />
                              )}
                              {field.post_processing.type === 'regex_replace' && (
                                <>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    label="Pattern"
                                    value={field.post_processing.pattern || ''}
                                    onChange={e =>
                                      updatePostProcessing('seller_page' as any, fieldKey, {
                                        pattern: e.target.value,
                                      })
                                    }
                                    placeholder="\\s+"
                                    helperText="Regular expression pattern"
                                    sx={{ mb: 1 }}
                                    InputProps={getRegexInputProps(
                                      field.post_processing.pattern,
                                      'Test regex replacement',
                                      'replace',
                                      field.post_processing.replacement || ''
                                    )}
                                  />
                                  <TextField
                                    fullWidth
                                    size="small"
                                    label="Replacement"
                                    value={field.post_processing.replacement || ''}
                                    onChange={e =>
                                      updatePostProcessing('seller_page' as any, fieldKey, {
                                        replacement: e.target.value,
                                      })
                                    }
                                    placeholder=" "
                                    helperText="Replacement string"
                                  />
                                </>
                              )}
                            </Box>
                          )}

                          {/* Click Configuration */}
                          <Grid item xs={12}>
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                mb: 1,
                              }}
                            >
                              <Typography variant="subtitle2">Click Navigation</Typography>
                              {field.click ? (
                                <Button
                                  onClick={() =>
                                    updateField('seller_page' as any, fieldKey, {
                                      click: undefined,
                                    })
                                  }
                                  size="small"
                                  color="error"
                                >
                                  Remove Click
                                </Button>
                              ) : (
                                <Button
                                  onClick={() =>
                                    updateField('seller_page' as any, fieldKey, {
                                      click: {
                                        selector: '',
                                        wait_for: 2.0,
                                        open_in_new_page: false,
                                      },
                                    })
                                  }
                                  size="small"
                                  startIcon={<AddIcon />}
                                >
                                  Add Click
                                </Button>
                              )}
                            </Box>
                            {field.click && (
                              <Box sx={{ pl: 2, borderLeft: '2px solid #e0e0e0' }}>
                                <Grid container spacing={2}>
                                  <Grid item xs={12} md={6}>
                                    <TextField
                                      fullWidth
                                      size="small"
                                      label="Click Selector"
                                      value={field.click.selector || ''}
                                      onChange={e =>
                                        updateField('seller_page' as any, fieldKey, {
                                          click: {
                                            ...field.click,
                                            selector: e.target.value,
                                          },
                                        })
                                      }
                                      placeholder="a[href*='contactinfo.html']"
                                      helperText="CSS selector for element to click"
                                      InputProps={getSelectorInputProps(field.click?.selector)}
                                    />
                                  </Grid>
                                  <Grid item xs={12} md={3}>
                                    <TextField
                                      fullWidth
                                      size="small"
                                      label="Wait Time (seconds)"
                                      type="number"
                                      value={field.click.wait_for || 2.0}
                                      onChange={e =>
                                        updateField('seller_page' as any, fieldKey, {
                                          click: {
                                            selector: field.click?.selector || '',
                                            wait_for: parseFloat(e.target.value) || 2.0,
                                            open_in_new_page:
                                              field.click?.open_in_new_page || false,
                                          },
                                        })
                                      }
                                      helperText="Time to wait after clicking"
                                    />
                                  </Grid>
                                  <Grid item xs={12} md={3}>
                                    <FormControlLabel
                                      control={
                                        <Switch
                                          checked={field.click.open_in_new_page || false}
                                          onChange={e =>
                                            updateField('seller_page' as any, fieldKey, {
                                              click: {
                                                selector: field.click?.selector || '',
                                                wait_for: field.click?.wait_for || 2.0,
                                                open_in_new_page: e.target.checked,
                                              },
                                            })
                                          }
                                        />
                                      }
                                      label="Open in New Page"
                                    />
                                  </Grid>
                                </Grid>
                              </Box>
                            )}
                          </Grid>
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Seller Navigation Configuration - removed duplicate below */}
        {/* Duplicate removed */}
        {/* END Seller Navigation duplicate */}
        {/* Seller Navigation Configuration */}
        {/* (section already rendered above) */}
        {/* Removing second instance to avoid duplication */}
        {false && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Seller Navigation
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Mode</InputLabel>
                      <Select
                        label="Mode"
                        value={(config as any)?.seller_navigation?.mode || 'multiple'}
                        onChange={e =>
                          setConfig(prev => ({
                            ...prev,
                            seller_navigation: {
                              ...(prev as any).seller_navigation,
                              mode: e.target.value,
                            },
                          }))
                        }
                      >
                        <MenuItem value="single">single</MenuItem>
                        <MenuItem value="multiple">multiple</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  {sellerNavigationMode !== 'single' && (
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Max Sellers"
                        value={(config as any)?.seller_navigation?.max_sellers ?? 10}
                        onChange={e =>
                          setConfig(prev => ({
                            ...prev,
                            seller_navigation: {
                              ...(prev as any).seller_navigation,
                              max_sellers: Number(e.target.value),
                            },
                          }))
                        }
                        inputProps={{ min: 1, max: 50 }}
                      />
                    </Grid>
                  )}
                </Grid>

                <Box sx={{ mt: 3 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 1,
                    }}
                  >
                    <Typography variant="subtitle1">From Product Steps</Typography>
                    <Button
                      startIcon={<AddIcon />}
                      size="small"
                      onClick={() =>
                        setConfig(prev => ({
                          ...prev,
                          seller_navigation: {
                            ...(prev as any).seller_navigation,
                            from_product: [
                              ...((prev as any).seller_navigation?.from_product || []),
                              { type: 'extract', selector: '', attribute: 'href' },
                            ],
                          },
                        }))
                      }
                    >
                      Add Step
                    </Button>
                  </Box>

                  {((config as any)?.seller_navigation?.from_product || []).map(
                    (step: any, idx: number) => (
                      <Accordion key={idx} sx={{ mb: 1 }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          Step {idx + 1}
                        </AccordionSummary>
                        <AccordionDetails>
                          <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={2}>
                              <FormControl fullWidth size="small">
                                <InputLabel>Type</InputLabel>
                                <Select
                                  label="Type"
                                  value={step.type || 'extract'}
                                  onChange={e => {
                                    const next = [
                                      ...((config as any).seller_navigation?.from_product || []),
                                    ];
                                    next[idx] = { ...step, type: e.target.value };
                                    setConfig((prev: any) => ({
                                      ...prev,
                                      seller_navigation: {
                                        ...prev.seller_navigation,
                                        from_product: next,
                                      },
                                    }));
                                  }}
                                >
                                  <MenuItem value="extract">extract</MenuItem>
                                  <MenuItem value="follow">follow</MenuItem>
                                  <MenuItem value="iterate">iterate</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Selector"
                                value={step.selector || ''}
                                onChange={e => {
                                  const next = [
                                    ...((config as any).seller_navigation?.from_product || []),
                                  ];
                                  next[idx] = { ...step, selector: e.target.value };
                                  setConfig((prev: any) => ({
                                    ...prev,
                                    seller_navigation: {
                                      ...prev.seller_navigation,
                                      from_product: next,
                                    },
                                  }));
                                }}
                                placeholder="a[href*='/aag/main'], a[href*='?me=']"
                                InputProps={getSelectorInputProps(step.selector)}
                              />
                            </Grid>
                            <Grid item xs={12} md={2}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Attribute"
                                value={step.attribute || 'href'}
                                onChange={e => {
                                  const next = [
                                    ...((config as any).seller_navigation?.from_product || []),
                                  ];
                                  next[idx] = { ...step, attribute: e.target.value };
                                  setConfig((prev: any) => ({
                                    ...prev,
                                    seller_navigation: {
                                      ...prev.seller_navigation,
                                      from_product: next,
                                    },
                                  }));
                                }}
                              />
                            </Grid>
                            <Grid item xs={12} md={2}>
                              <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label="Limit"
                                value={step.limit ?? ''}
                                onChange={e => {
                                  const next = [
                                    ...((config as any).seller_navigation?.from_product || []),
                                  ];
                                  const v =
                                    e.target.value === '' ? undefined : Number(e.target.value);
                                  next[idx] = { ...step, limit: v };
                                  setConfig((prev: any) => ({
                                    ...prev,
                                    seller_navigation: {
                                      ...prev.seller_navigation,
                                      from_product: next,
                                    },
                                  }));
                                }}
                                helperText="Used by iterate"
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                  size="small"
                                  color="error"
                                  onClick={() => {
                                    const next = [
                                      ...((config as any).seller_navigation?.from_product || []),
                                    ];
                                    next.splice(idx, 1);
                                    setConfig((prev: any) => ({
                                      ...prev,
                                      seller_navigation: {
                                        ...prev.seller_navigation,
                                        from_product: next,
                                      },
                                    }));
                                  }}
                                >
                                  Remove Step
                                </Button>
                              </Box>
                            </Grid>
                          </Grid>
                        </AccordionDetails>
                      </Accordion>
                    )
                  )}
                </Box>

                {/* Test URLs for AI Validation - Seller Page */}
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" sx={{ mb: 2 }}>
                    Test URLs for AI Validation
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Specific seller URLs to test selectors against during AI validation. These URLs
                    should contain the seller fields you're trying to extract.
                  </Typography>
                  <Button
                    startIcon={<AddIcon />}
                    size="small"
                    onClick={() =>
                      setConfig(prev => ({
                        ...prev,
                        pages: {
                          ...prev.pages,
                          seller_page: {
                            ...prev.pages?.seller_page,
                            page_type: 'seller_page',
                            test_urls: [
                              ...(((prev.pages?.seller_page as any)?.test_urls || []) as string[]),
                              '',
                            ],
                          } as any,
                        },
                      }))
                    }
                  >
                    Add Test URL
                  </Button>
                  {(((config.pages?.seller_page as any) || {}).test_urls || []).map(
                    (url: string, idx: number) => (
                      <Box
                        key={idx}
                        sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center', mt: 1 }}
                      >
                        <TextField
                          fullWidth
                          size="small"
                          label={`Test URL ${idx + 1}`}
                          value={url}
                          onChange={e =>
                            setConfig(prev => ({
                              ...prev,
                              pages: {
                                ...prev.pages,
                                seller_page: {
                                  ...prev.pages?.seller_page,
                                  page_type: 'seller_page',
                                  test_urls: (
                                    ((prev.pages?.seller_page as any)?.test_urls || []) as string[]
                                  ).map((u, i) => (i === idx ? e.target.value : u)),
                                } as any,
                              },
                            }))
                          }
                          placeholder="https://www.ebay.com/usr/seller-name"
                          helperText="Seller URL to test selectors against"
                        />
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() =>
                            setConfig(prev => ({
                              ...prev,
                              pages: {
                                ...prev.pages,
                                seller_page: {
                                  ...prev.pages?.seller_page,
                                  page_type: 'seller_page',
                                  test_urls: (
                                    ((prev.pages?.seller_page as any)?.test_urls || []) as string[]
                                  ).filter((_, i) => i !== idx),
                                } as any,
                              },
                            }))
                          }
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    )
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Headers Configuration */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Typography variant="h6">HTTP Headers</Typography>
                <Button startIcon={<AddIcon />} onClick={addHeader} size="small">
                  Add Additional Header
                </Button>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                HTTP headers are sent with every request to mimic browser behavior and avoid
                detection. Common headers include Accept-Language, Referer, and custom
                authentication tokens.
              </Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={config.use_common_headers !== false}
                    onChange={e =>
                      setConfig(prev => ({ ...prev, use_common_headers: e.target.checked }))
                    }
                  />
                }
                label="Use common headers from Settings"
                sx={{ mb: 2 }}
              />

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Additional headers specific to this marketplace (will override common headers if
                same key exists)
              </Typography>

              {Object.entries(config.additional_headers || {}).map(([key, value]) => (
                <Box key={key} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                  <TextField
                    label="Header Name"
                    value={key}
                    onChange={e => {
                      const newHeaders = { ...config.additional_headers };
                      delete newHeaders[key];
                      if (e.target.value && value) {
                        newHeaders[e.target.value] = value;
                      }
                      setConfig(prev => ({ ...prev, additional_headers: newHeaders }));
                    }}
                    size="small"
                    sx={{ minWidth: 200 }}
                  />
                  <TextField
                    label="Header Value"
                    value={value}
                    onChange={e => updateHeader(key, e.target.value)}
                    size="small"
                    sx={{ flex: 1 }}
                  />
                  <IconButton onClick={() => removeHeader(key)} size="small" color="error">
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>

        {/* User Agents */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Typography variant="h6">User Agents</Typography>
                <Button startIcon={<AddIcon />} onClick={addUserAgent} size="small">
                  Add Additional User Agent
                </Button>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                User agent strings identify the browser type to the server. Rotating between
                different user agents helps avoid rate limiting and detection. The scraper randomly
                selects from the configured list.
              </Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={config.use_common_user_agents !== false}
                    onChange={e =>
                      setConfig(prev => ({ ...prev, use_common_user_agents: e.target.checked }))
                    }
                  />
                }
                label="Use common user agents from Settings"
                sx={{ mb: 2 }}
              />

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Additional user agents specific to this marketplace (will be combined with common
                user agents)
              </Typography>

              {(config.additional_user_agents || []).map((agent, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                  <TextField
                    fullWidth
                    label={`Additional User Agent ${index + 1}`}
                    value={agent}
                    onChange={e => updateUserAgent(index, e.target.value)}
                    size="small"
                    placeholder="Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36"
                  />
                  <IconButton onClick={() => removeUserAgent(index)} size="small" color="error">
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>

        {/* JSON Preview - include locale/country/currency so copy matches form and saved config */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Typography variant="h6">Configuration Preview (JSON)</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    startIcon={<PasteIcon />}
                    onClick={handlePasteDefinition}
                    size="small"
                    color="info"
                  >
                    Paste JSON
                  </Button>
                  <Button
                    startIcon={<CopyIcon />}
                    onClick={() => {
                      const forCopy = {
                        ...config,
                        ...(definition.locale != null &&
                          definition.locale !== '' && { locale: definition.locale }),
                        ...(definition.country != null &&
                          definition.country !== '' && { country: definition.country }),
                        ...(definition.currency != null &&
                          definition.currency !== '' && { currency: definition.currency }),
                      };
                      navigator.clipboard.writeText(JSON.stringify(forCopy, null, 2));
                    }}
                    size="small"
                  >
                    Copy JSON
                  </Button>
                </Box>
              </Box>

              <TextField
                fullWidth
                multiline
                minRows={8}
                maxRows={15}
                value={JSON.stringify(
                  {
                    ...config,
                    ...(definition.locale != null &&
                      definition.locale !== '' && { locale: definition.locale }),
                    ...(definition.country != null &&
                      definition.country !== '' && { country: definition.country }),
                    ...(definition.currency != null &&
                      definition.currency !== '' && { currency: definition.currency }),
                  },
                  null,
                  2
                )}
                InputProps={{
                  readOnly: true,
                  sx: {
                    fontFamily:
                      'Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                    fontSize: '0.875rem',
                  },
                }}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* AI Validation - At bottom of page */}
      <Accordion
        expanded={aiAccordionExpanded}
        onChange={(_, expanded) => setAiAccordionExpanded(expanded)}
        sx={{ mt: 3 }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            bgcolor: isAiValidating
              ? 'primary.dark'
              : validationError
                ? 'error.dark'
                : validationSummary
                  ? validationSummary.broken_selectors > 0
                    ? 'warning.dark'
                    : 'success.dark'
                  : 'background.paper',
            color:
              isAiValidating || validationSummary || validationError
                ? 'primary.contrastText'
                : 'text.primary',
            '&:hover': {
              bgcolor: isAiValidating
                ? 'primary.dark'
                : validationError
                  ? 'error.dark'
                  : validationSummary
                    ? validationSummary.broken_selectors > 0
                      ? 'warning.dark'
                      : 'success.dark'
                    : 'action.hover',
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <Typography variant="h6">AI Validation</Typography>
            {isAiValidating && <Chip label="Running..." size="small" color="primary" />}
            {validationError && !isAiValidating && (
              <Chip label="Failed" size="small" color="error" />
            )}
            {validationSummary && !validationError && !isAiValidating && (
              <Chip
                label={validationSummary.broken_selectors > 0 ? 'Issues Found' : 'Complete'}
                size="small"
                color={validationSummary.broken_selectors > 0 ? 'warning' : 'success'}
              />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {/* Run AI Validation Button */}
          <Box sx={{ mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<AutoFixHighIcon />}
              onClick={performAiValidation}
              disabled={isAiValidating || !definition.domain || !hasTestUrls()}
              color="secondary"
              title={
                !hasTestUrls()
                  ? 'Add test URLs below before running validation'
                  : 'Run AI validation to test selectors and get improvement suggestions'
              }
            >
              {isAiValidating ? (
                <>
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                  AI Validating...
                </>
              ) : (
                'Run AI Validation'
              )}
            </Button>
            {!hasTestUrls() && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                Add test URLs in the page configurations below to enable validation
              </Typography>
            )}
          </Box>

          {validationError && !isAiValidating && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                Validation Failed
              </Typography>
              <Typography variant="body2">{validationError}</Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Check the browser console and backend logs for more details.
              </Typography>
            </Alert>
          )}

          {isAiValidating && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                bgcolor: 'background.default',
                borderRadius: 1,
                border: 1,
                borderColor: 'divider',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1,
                }}
              >
                <Typography variant="body2">{validationStep || 'Analyzing...'}</Typography>
                <Button
                  size="small"
                  color="error"
                  variant="outlined"
                  onClick={cancelAiValidation}
                  startIcon={<CancelIcon />}
                >
                  Cancel
                </Button>
              </Box>
              <LinearProgress
                variant="determinate"
                value={Math.max(5, Math.min(100, Math.round((validationProgress || 0) * 100)))}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {Math.round((validationProgress || 0) * 100)}% complete • No timeout - validation
                will run until complete
              </Typography>
            </Box>
          )}

          {!isAiValidating && validationSummary && !validationError && (
            <>
              <Alert
                severity={validationSummary.broken_selectors > 0 ? 'warning' : 'success'}
                sx={{ mb: 2 }}
              >
                {validationSummary.broken_selectors > 0
                  ? `AI validation completed with ${validationSummary.broken_selectors} issue(s). Check the summary below.`
                  : 'AI validation completed successfully! All selectors are working.'}
              </Alert>

              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  bgcolor: 'background.default',
                  borderRadius: 1,
                  border: 1,
                  borderColor: 'divider',
                }}
              >
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                  Analysis Summary
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary">
                      Selectors Tested
                    </Typography>
                    <Typography variant="h6">{validationSummary.total_selectors_tested}</Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary">
                      Working
                    </Typography>
                    <Typography variant="h6" color="success.main">
                      {validationSummary.working_selectors}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary">
                      Broken
                    </Typography>
                    <Typography variant="h6" color="error.main">
                      {validationSummary.broken_selectors}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary">
                      Coverage
                    </Typography>
                    <Typography variant="h6">
                      {Math.round((validationSummary.coverage_score || 0) * 100)}%
                    </Typography>
                  </Grid>
                </Grid>

                {validationSuggestions && validationSuggestions.length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                      AI Suggestions ({validationSuggestions.length})
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mb: 2 }}
                    >
                      Each suggestion is advisory. Approve to apply it to your local definition (you
                      still need to click Save to persist). Dismiss to hide it without changes.
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {validationSuggestions.map((s, idx) => {
                        const isApplied = appliedSuggestions.has(idx);
                        const isDismissed = dismissedSuggestions.has(idx);
                        const isActionable = !!(s.page && s.field && s.suggested_selector);
                        const severityColor =
                          s.severity === 'critical' || s.severity === 'high'
                            ? 'error.main'
                            : s.severity === 'medium'
                              ? 'warning.main'
                              : 'text.secondary';
                        return (
                          <Box
                            key={idx}
                            sx={{
                              border: 1,
                              borderColor: isApplied
                                ? 'success.main'
                                : isDismissed
                                  ? 'grey.300'
                                  : 'divider',
                              borderRadius: 1,
                              p: 2,
                              opacity: isDismissed ? 0.5 : 1,
                              backgroundColor: isApplied ? 'success.light' : 'background.paper',
                              transition: 'opacity 0.2s, border-color 0.2s',
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'flex-start',
                                gap: 2,
                                mb: 1,
                              }}
                            >
                              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <Chip label={s.type} size="small" variant="outlined" />
                                <Chip
                                  label={s.severity}
                                  size="small"
                                  sx={{ color: severityColor, borderColor: severityColor }}
                                  variant="outlined"
                                />
                                {s.page && <Chip label={s.page} size="small" variant="outlined" />}
                                {s.field && (
                                  <Chip label={s.field} size="small" variant="outlined" />
                                )}
                                {typeof s.confidence === 'number' && (
                                  <Chip
                                    label={`${Math.round(s.confidence * 100)}% confident`}
                                    size="small"
                                    variant="outlined"
                                  />
                                )}
                              </Box>
                              <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                                {!isApplied && !isDismissed && (
                                  <>
                                    <Button
                                      size="small"
                                      variant="contained"
                                      color="primary"
                                      disabled={!isActionable}
                                      onClick={() => applySuggestion(idx, s)}
                                    >
                                      {isActionable ? 'Approve' : 'Advisory'}
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      onClick={() => dismissSuggestion(idx)}
                                    >
                                      Dismiss
                                    </Button>
                                  </>
                                )}
                                {isApplied && <Chip label="Applied" size="small" color="success" />}
                                {isDismissed && <Chip label="Dismissed" size="small" />}
                              </Box>
                            </Box>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              {s.reason}
                            </Typography>
                            {(s.current_selector || s.suggested_selector) && (
                              <Box
                                sx={{
                                  display: 'grid',
                                  gridTemplateColumns: '80px 1fr',
                                  gap: 0.5,
                                  fontFamily: 'monospace',
                                  fontSize: '0.85em',
                                  backgroundColor: 'grey.100',
                                  borderRadius: 0.5,
                                  p: 1,
                                  mt: 1,
                                }}
                              >
                                {s.current_selector && (
                                  <>
                                    <Box sx={{ color: 'error.main', fontWeight: 'bold' }}>
                                      Current:
                                    </Box>
                                    <Box
                                      sx={{
                                        color: 'error.main',
                                        wordBreak: 'break-all',
                                      }}
                                    >
                                      {s.current_selector}
                                    </Box>
                                  </>
                                )}
                                {s.suggested_selector && (
                                  <>
                                    <Box sx={{ color: 'success.main', fontWeight: 'bold' }}>
                                      Suggested:
                                    </Box>
                                    <Box
                                      sx={{
                                        color: 'success.main',
                                        wordBreak: 'break-all',
                                      }}
                                    >
                                      {s.suggested_selector}
                                    </Box>
                                  </>
                                )}
                              </Box>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                )}

                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    onClick={() => {
                      setValidationSummary(null);
                      setValidationSuggestions(null);
                    }}
                  >
                    Dismiss
                  </Button>
                </Box>
              </Box>
            </>
          )}

          {/* Test URLs Configuration - Always visible */}
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Test URLs for AI Validation
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Provide specific URLs to test selectors against during AI validation. These should be
              real pages with the data you want to extract.
            </Typography>

            {/* Search Results Test URLs */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                Search Results Pages
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                URLs of search result pages (e.g., search for "starbucks" or "keychain")
              </Typography>
              <Button
                startIcon={<AddIcon />}
                size="small"
                onClick={() =>
                  setConfig(prev => ({
                    ...prev,
                    pages: {
                      ...prev.pages,
                      search_results: {
                        ...prev.pages?.search_results,
                        page_type: 'search_results',
                        test_urls: [
                          ...(((prev.pages?.search_results as any)?.test_urls || []) as string[]),
                          '',
                        ],
                      } as any,
                    },
                  }))
                }
              >
                Add Search URL
              </Button>
              {(((config.pages?.search_results as any) || {}).test_urls || []).map(
                (url: string, idx: number) => (
                  <Box
                    key={idx}
                    sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center', mt: 1 }}
                  >
                    <TextField
                      fullWidth
                      size="small"
                      label={`Test URL ${idx + 1}`}
                      value={url}
                      onChange={e =>
                        setConfig(prev => ({
                          ...prev,
                          pages: {
                            ...prev.pages,
                            search_results: {
                              ...prev.pages?.search_results,
                              page_type: 'search_results',
                              test_urls: (
                                ((prev.pages?.search_results as any)?.test_urls || []) as string[]
                              ).map((u, i) => (i === idx ? e.target.value : u)),
                            } as any,
                          },
                        }))
                      }
                      placeholder="https://www.ebay.com/sch/i.html?_nkw=starbucks"
                    />
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() =>
                        setConfig(prev => ({
                          ...prev,
                          pages: {
                            ...prev.pages,
                            search_results: {
                              ...prev.pages?.search_results,
                              page_type: 'search_results',
                              test_urls: (
                                ((prev.pages?.search_results as any)?.test_urls || []) as string[]
                              ).filter((_, i) => i !== idx),
                            } as any,
                          },
                        }))
                      }
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                )
              )}
            </Box>

            {/* Product Listing Test URLs */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                Product Listing Pages
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                URLs of individual product pages
              </Typography>
              <Button
                startIcon={<AddIcon />}
                size="small"
                onClick={() =>
                  setConfig(prev => ({
                    ...prev,
                    pages: {
                      ...prev.pages,
                      product_listing: {
                        ...prev.pages?.product_listing,
                        page_type: 'product_listing',
                        test_urls: [
                          ...(((prev.pages?.product_listing as any)?.test_urls || []) as string[]),
                          '',
                        ],
                      } as any,
                    },
                  }))
                }
              >
                Add Product URL
              </Button>
              {(((config.pages?.product_listing as any) || {}).test_urls || []).map(
                (url: string, idx: number) => (
                  <Box
                    key={idx}
                    sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center', mt: 1 }}
                  >
                    <TextField
                      fullWidth
                      size="small"
                      label={`Test URL ${idx + 1}`}
                      value={url}
                      onChange={e =>
                        setConfig(prev => ({
                          ...prev,
                          pages: {
                            ...prev.pages,
                            product_listing: {
                              ...prev.pages?.product_listing,
                              page_type: 'product_listing',
                              test_urls: (
                                ((prev.pages?.product_listing as any)?.test_urls || []) as string[]
                              ).map((u, i) => (i === idx ? e.target.value : u)),
                            } as any,
                          },
                        }))
                      }
                      placeholder="https://www.ebay.com/itm/123456789"
                    />
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() =>
                        setConfig(prev => ({
                          ...prev,
                          pages: {
                            ...prev.pages,
                            product_listing: {
                              ...prev.pages?.product_listing,
                              page_type: 'product_listing',
                              test_urls: (
                                ((prev.pages?.product_listing as any)?.test_urls || []) as string[]
                              ).filter((_, i) => i !== idx),
                            } as any,
                          },
                        }))
                      }
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                )
              )}
            </Box>

            {/* Seller Page Test URLs */}
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                Seller Pages
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                URLs of seller profile pages
              </Typography>
              <Button
                startIcon={<AddIcon />}
                size="small"
                onClick={() =>
                  setConfig(prev => ({
                    ...prev,
                    pages: {
                      ...prev.pages,
                      seller_page: {
                        ...prev.pages?.seller_page,
                        page_type: 'seller_page',
                        test_urls: [
                          ...(((prev.pages?.seller_page as any)?.test_urls || []) as string[]),
                          '',
                        ],
                      } as any,
                    },
                  }))
                }
              >
                Add Seller URL
              </Button>
              {(((config.pages?.seller_page as any) || {}).test_urls || []).map(
                (url: string, idx: number) => (
                  <Box
                    key={idx}
                    sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center', mt: 1 }}
                  >
                    <TextField
                      fullWidth
                      size="small"
                      label={`Test URL ${idx + 1}`}
                      value={url}
                      onChange={e =>
                        setConfig(prev => ({
                          ...prev,
                          pages: {
                            ...prev.pages,
                            seller_page: {
                              ...prev.pages?.seller_page,
                              page_type: 'seller_page',
                              test_urls: (
                                ((prev.pages?.seller_page as any)?.test_urls || []) as string[]
                              ).map((u, i) => (i === idx ? e.target.value : u)),
                            } as any,
                          },
                        }))
                      }
                      placeholder="https://www.ebay.com/usr/seller-name"
                    />
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() =>
                        setConfig(prev => ({
                          ...prev,
                          pages: {
                            ...prev.pages,
                            seller_page: {
                              ...prev.pages?.seller_page,
                              page_type: 'seller_page',
                              test_urls: (
                                ((prev.pages?.seller_page as any)?.test_urls || []) as string[]
                              ).filter((_, i) => i !== idx),
                            } as any,
                          },
                        }))
                      }
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                )
              )}
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Paste Definition Dialog */}
      <Dialog open={showPasteDialog} onClose={handlePasteCancel} maxWidth="md" fullWidth>
        <DialogTitle>Paste Marketplace Definition</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Paste a JSON marketplace definition below. This will populate all form fields with the
            definition data.
          </Typography>
          <TextField
            multiline
            rows={12}
            fullWidth
            variant="outlined"
            placeholder="Paste your marketplace definition JSON here..."
            value={pasteText}
            onChange={e => setPasteText(e.target.value)}
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.875rem',
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePasteCancel}>Cancel</Button>
          <Button onClick={handlePasteConfirm} variant="contained" disabled={!pasteText.trim()}>
            Apply Definition
          </Button>
        </DialogActions>
      </Dialog>

      {/* Version History Dialog */}
      <Dialog
        open={showVersionHistory}
        onClose={() => setShowVersionHistory(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Version History</DialogTitle>
        <DialogContent>
          {isLoadingVersions ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : versions.length === 0 ? (
            <Typography color="text.secondary" sx={{ p: 2 }}>
              No version history available yet. Versions are saved each time you update the
              definition.
            </Typography>
          ) : (
            <Box sx={{ mt: 2 }}>
              {versions.map((version, index) => (
                <Card key={version.id} sx={{ mb: 2 }}>
                  <CardContent>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        mb: 2,
                      }}
                    >
                      <Box>
                        <Typography variant="h6">
                          Version {version.version_number}
                          {index === 0 && (
                            <Chip label="Current" size="small" color="primary" sx={{ ml: 1 }} />
                          )}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {new Date(version.created_at).toLocaleString()}
                        </Typography>
                        {version.change_reason && (
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            <strong>Reason:</strong> {version.change_reason}
                          </Typography>
                        )}
                        <Typography variant="body2" color="text.secondary">
                          <strong>Changed by:</strong> {version.changed_by}
                        </Typography>
                      </Box>
                      {index !== 0 && (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              setSelectedVersion(version);
                            }}
                          >
                            View Config
                          </Button>
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<RestoreIcon />}
                            onClick={() => handleRollback(version.id)}
                            disabled={isLoading}
                          >
                            Rollback
                          </Button>
                        </Box>
                      )}
                    </Box>
                    {selectedVersion?.id === version.id && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          Configuration:
                        </Typography>
                        <TextField
                          fullWidth
                          multiline
                          minRows={8}
                          maxRows={20}
                          value={JSON.stringify(version.config, null, 2)}
                          InputProps={{
                            readOnly: true,
                            sx: {
                              fontFamily: 'monospace',
                              fontSize: '0.75rem',
                            },
                          }}
                        />
                      </Box>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowVersionHistory(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Regex Tester Dialog */}
      <RegexTesterDialog
        open={regexTesterOpen}
        onClose={() => {
          setRegexTesterOpen(false);
          setRegexTesterMatchOnlyAtStart(false);
        }}
        pattern={regexTesterPattern}
        title={regexTesterTitle}
        mode={regexTesterMode}
        replacement={regexTesterReplacement}
        matchOnlyAtStart={regexTesterMatchOnlyAtStart}
        domain={definition.domain}
      />
    </Box>
  );
};

export default DefinitionEditor;
